// ═══════════════════════════════════════════════════════════════════
// fx3D.js — THE FX VOCABULARY · H23 SHIP 1 → H24 · Terror TCG
// Thirty-two primitives. Every card animation in the game is a chord
// played on these, and nothing else.
// ═══════════════════════════════════════════════════════════════════
//
// caP'n's ask (July 27, minted as H23): "read every single card and
// truly think about them and their effects, the ilk, their theme, and
// let's get super unique animations for every single card and
// interaction... focus on small well thought out and well implemented
// and fully designed 3D unique animations."
//
// ── WHY THIS FILE EXISTS AT ALL ────────────────────────────────
// H21 shipped the STRIKE LANGUAGE: a taxonomy of eight arrivals, four
// Ilk strikes and four wounds, all expressed as POSE — where the card
// moves. That was the right first ship and it is still the floor
// underneath this one. But pose alone can only ever say so much: a
// Spectral Tiger leaning across the seam and a Chaos Dragon leaning
// across the seam are the same picture with different numbers on it.
//
// The thing that makes a Pounce read as a POUNCE is not the lean. It
// is the three claw-marks that open across the victim. H21 knew this —
// it DECLARED accents (`lance` / `motes` / `rake` / `echo`) on every
// strike profile — and then rendered exactly none of them, because
// there was no vocabulary to render them WITH. This file is that
// vocabulary, and the H21 remainder item (a) closes with it.
//
// ── THE LAW THIS FILE AMENDS, ON PURPOSE ───────────────────────
// strikeLanguage.js Law 2 reads: "ONE REGISTRY, ZERO PER-CARD CODE."
// caP'n has deliberately overruled it for H23. It was the correct law
// for 8 profiles and the wrong law for 181 cards. So it is AMENDED,
// not deleted, and the amendment is precise:
//
//   THE TAXONOMY IS NO LONGER THE ANSWER. IT IS THE FLOOR.
//
// Every card still resolves to a sane profile the day it is minted,
// with zero authoring. Cards we have WRITTEN get their own score on
// top. Nothing regresses; the ceiling just came off.
//
// ── THE THREE LAWS THIS SHIPS UNDER (all inherited, none relaxed) ──
//
//  1. THE PURE-RENDERER CONTRACT. Every primitive here is a RESPONSE
//     to a state change the engine already made. Nothing is awaited,
//     nothing gates a rule. Delete this file mid-match and the duel
//     resolves identically — it just goes quiet.
//
//  2. THE PERF LAW (ie-realtime-perf). No setState in useFrame, ever.
//     Every primitive animates imperatively on refs, allocates its
//     geometry ONCE in a useMemo, and self-retires through a single
//     onDone callback fired on the frame it finishes. A cue that is
//     not yet due is mounted but invisible — no timers, no scheduler,
//     no re-render storm at the start of a chord.
//
//  3. THE DIAL STILL RULES (T17). Every duration on this page is
//     multiplied by motionRate(), so QUICK runs the same chord faster
//     rather than a different one, and at OFF the runner never mounts
//     a cue at all. An animation system without its off-switch is a
//     feature you can't undo.
//
// ── ANATOMY OF A CUE ───────────────────────────────────────────
//   { fx, at, ms, color, from, to, n, r, size, spin, tone }
//     fx     which primitive plays        (the twelve names below)
//     at     ms from the START of the score before this cue fires
//     ms     how long this cue itself runs
//     from   world [x,y,z] — resolved by cardMotion, never a string here
//     to     world [x,y,z] — the destination / focus
//     n      count (motes, shards, slashes, stream density)
//     r      radius / reach / spread, in world units
//     size   thickness or glyph size
//     spin   revolutions over the cue's life
//     tone   free-form modifier a primitive may read (e.g. slash angle)
//
// A SCORE is { name, ms, cues[] }. `ms` is the score's own length and
// is what the turn clock holds for — see strikeLanguage's HOLD.
//
// ═══════════════════════════════════════════════════════════════════
import { memo, useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame, useThree } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import { motionRate } from "./strikeLanguage";
import { GM } from "./theme";
import { Y } from "./fieldLayout3D";

// ═══════════════════════════════════════════════════════════════════
// PART I — THE CUE CLOCK
// ═══════════════════════════════════════════════════════════════════
// One helper, called at the top of every primitive's useFrame. It owns
// the delay, the progress, the rate multiplier and the retirement, so
// no primitive below ever writes that logic twice (and no primitive
// can forget to call onDone and leak a mesh into the scene forever).
//
// Returns:
//   -1  not due yet — the primitive must stay invisible
//  0..1 live — the primitive's own timeline
//    1  the final frame; onDone has already been fired
//
// dt is clamped at 50ms. A tab that was backgrounded for four seconds
// must not teleport a chord to its last frame — it plays out from
// wherever it was, which is both cheaper and better-looking than a
// catch-up snap.

function cueTick(st, dt, cue, onDone) {
  const s = st.current;
  if (s.done) return 1;
  const rate = s.rate || 1;
  s.age = (s.age || 0) + Math.min(dt, 0.05) * rate;
  const delay = (cue.at || 0) / 1000;
  if (s.age < delay) return -1;
  const span = Math.max(0.06, (cue.ms || 400) / 1000);
  const t = (s.age - delay) / span;
  if (t >= 1) {
    s.done = true;
    if (onDone) onDone(cue.id);
    return 1;
  }
  return t;
}

/** Fresh per-cue clock state. `rate` is baked in at mount so a player
 *  flipping the dial mid-chord doesn't change speed halfway through a
 *  swing (which reads as a stutter, not as a setting). */
const newClock = () => ({ age: 0, done: false, rate: motionRate() || 1 });

/**
 * ── THE DISPOSAL LAW ───────────────────────────────────────────────
 * Every primitive here builds its own geometry, and every primitive
 * here UNMOUNTS — that is the whole point of a one-shot. three.js does
 * not free GPU buffers when React drops a node, so a bloody duel would
 * quietly strand a few hundred BufferGeometries on the card. Anything
 * built in a useMemo on this page gets handed to this hook, and the
 * ONLY correct place for that cleanup is a useEffect: a memo'd cleanup
 * function is a function that is never called.
 *
 * Materials declared as JSX children (`<meshBasicMaterial …/>`) are
 * R3F's to free and must NOT be listed here.
 */
function useDispose(...objs) {
  useEffect(() => () => {
    objs.forEach((o) => {
      if (!o) return;
      try {
        if (o.geometry) o.geometry.dispose();
        if (o.material) o.material.dispose();
        if (o.dispose && !o.geometry && !o.material) o.dispose();
      } catch (_) { /* a failed dispose never breaks a frame */ }
    });
    // objs is a fresh array each render but its CONTENTS are memo'd,
    // which is what the cleanup actually closes over.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, objs);
}

// Shared easing. Deliberately the same four curves the Strike Language
// uses, so a cue and the pose it accompanies bend together.
const E = {
  out: (t) => 1 - Math.pow(1 - t, 3),
  in2: (t) => t * t,
  bump: (t) => Math.sin(t * Math.PI),
  snap: (t) => Math.sin(t * Math.PI * 0.5),
  flick: (t) => (t < 0.18 ? t / 0.18 : Math.max(0, 1 - (t - 0.18) / 0.82)),

  // ── H24 — THE THREE THAT MAKE IT FEEL PHYSICAL ─────────────────
  // The five above are all monotone approaches: they arrive at 1 and
  // stop. That is correct for light, which is why they were the whole
  // set for the strike language. It is wrong for anything with MASS,
  // and half the H24 vocabulary has mass — a coin lands, a lattice
  // snaps square, a dome slams up.
  //
  // The difference is entirely in the last 15% of the curve. A thing
  // that decelerates smoothly into its final position was placed
  // there; a thing that overshoots a hair and settles ARRIVED. Nobody
  // consciously sees the overshoot. Everybody feels its absence.

  /** Overshoots ~7% and settles. For anything that lands. */
  back: (t) => {
    const s = 1.70158 * 0.42;   // dial — deliberately gentle; a big
    const u = t - 1;            // overshoot reads as cartoon bounce
    return u * u * ((s + 1) * u + s) + 1;
  },

  /** Winds up slightly BACKWARD before going. Anticipation. */
  anticip: (t) => (t < 0.22
    ? -0.14 * Math.sin((t / 0.22) * Math.PI)
    : E.out((t - 0.22) / 0.78)),

  /** Harder shoulder than `out` — for things that stop dead. */
  expo: (t) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t)),
};

const V = (a) => new THREE.Vector3(a?.[0] || 0, a?.[1] || 0, a?.[2] || 0);
const lerp3 = (a, b, k) => [
  a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k,
];

// One PRNG per cue id, so every mote pattern is identical on both
// clients forever (PRNG LAW — never Math.random in a render path).
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const seedOf = (str) => {
  let h = 9; const s = String(str || "fx");
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 387420489);
  return h >>> 0;
};
const rnd = (str) => mulberry32(seedOf(str));

// Additive glow is the house look for every primitive here: these are
// LIGHT, not objects, and light adds. depthWrite off so two cues in
// the same chord never punch holes in each other.
const glowMat = (color, opacity = 1) => ({
  color, transparent: true, opacity, depthWrite: false,
  toneMapped: false, blending: THREE.AdditiveBlending,
  side: THREE.DoubleSide,
});

// ═══════════════════════════════════════════════════════════════════
// PART I.5 — THE FALLOFF (H24 · the crispness pass)
// ═══════════════════════════════════════════════════════════════════
// caP'n (Aug 3): "update the visual quality, animations, smoothness,
// crispness, and just give it that classic overhaul we always do."
//
// ── THE BUG THIS FIXES, WHICH HAD BEEN SHIPPING SINCE H23 ──────────
// `THREE.PointsMaterial` with no `map` draws every particle as a HARD
// SQUARE. Not a soft dot — a literal axis-aligned square of flat
// colour. Every mote in the game was one: Nine Shadows, The Swarm, the
// Rite of Return, every Anima gain, every dissolve. At small sizes on
// a dark felt it reads as "cheap sparkle" and it is very hard to
// consciously notice, because the eye reports the COLOUR and quietly
// files the shape under "particles look like that." They don't.
//
// The same class of problem sat on `Bloom` — the single most-used
// primitive in the file. A `circleGeometry` with flat additive colour
// is a DISC with a hard rim, not a glow. Every impact in the game was
// punctuated by a crisp-edged coin of light.
//
// Both are fixed by giving the material something to fall off with.
//
// ── WHY CANVAS AND NOT A FILE ──────────────────────────────────────
// A .png would mean a CDN round-trip, a loading state, and a frame
// where the board renders squares anyway (ie-cdn-assets exists to make
// that kind of thing survivable, and the answer here is simply to not
// need it). These are four tiny procedural gradients; they cost
// microseconds to draw once and they are then shared by every cue for
// the life of the page.
//
// ── THE SINGLETON LAW ──────────────────────────────────────────────
// Built ONCE, lazily, module-level, and never disposed — they outlive
// every cue by design. Generating one per cue would be a texture upload
// per particle burst, which is exactly the kind of thing that turns a
// smooth board into a hitching one (ie-realtime-perf: the bugs there
// are invisible by inspection and cost a session to find).
//
// Guarded for a document-less environment (SSR, a test harness, the
// node-side balancer) — a null map is simply the old hard-edged look,
// never a crash.

function makeTex(size, paint) {
  try {
    if (typeof document === "undefined") return null;
    const c = document.createElement("canvas");
    c.width = size; c.height = size;
    const g = c.getContext("2d");
    if (!g) return null;
    paint(g, size);
    const t = new THREE.CanvasTexture(c);
    t.needsUpdate = true;
    // No mips and linear filtering: these are always drawn near-screen
    // -space and mipmapping a 64px gradient just makes it mushy.
    t.minFilter = THREE.LinearFilter;
    t.magFilter = THREE.LinearFilter;
    t.generateMipmaps = false;
    return t;
  } catch (_) { return null; }
}

let _spark, _glow, _blade, _ring;

/**
 * THE SPARK — a soft round dot for every points cloud in the game.
 * The stop at 0.35 is doing the real work: a plain linear ramp reads as
 * fog, while a bright core with a fast shoulder reads as a PARTICLE.
 */
export function sparkTex() {
  if (_spark !== undefined) return _spark;
  _spark = makeTex(64, (g, s) => {
    const r = s / 2;
    const grad = g.createRadialGradient(r, r, 0, r, r, r);
    grad.addColorStop(0.00, "rgba(255,255,255,1)");
    grad.addColorStop(0.35, "rgba(255,255,255,0.62)");
    grad.addColorStop(0.70, "rgba(255,255,255,0.12)");
    grad.addColorStop(1.00, "rgba(255,255,255,0)");
    g.fillStyle = grad;
    g.fillRect(0, 0, s, s);
  });
  return _spark;
}

/**
 * THE GLOW — a wide soft falloff for blooms and star cores. Softer
 * shoulder than the spark, because this one is meant to read as
 * atmosphere around a bright point rather than as an object.
 */
export function glowTex() {
  if (_glow !== undefined) return _glow;
  _glow = makeTex(128, (g, s) => {
    const r = s / 2;
    const grad = g.createRadialGradient(r, r, 0, r, r, r);
    grad.addColorStop(0.00, "rgba(255,255,255,1)");
    grad.addColorStop(0.18, "rgba(255,255,255,0.78)");
    grad.addColorStop(0.45, "rgba(255,255,255,0.26)");
    grad.addColorStop(0.75, "rgba(255,255,255,0.05)");
    grad.addColorStop(1.00, "rgba(255,255,255,0)");
    g.fillStyle = grad;
    g.fillRect(0, 0, s, s);
  });
  return _glow;
}

/**
 * THE BLADE — an anisotropic streak: hot along the middle, gone at
 * both ends and both edges. This is what turns Star's crossed
 * rectangles into an actual flare and stops Sever and Rift from
 * reading as bright tape.
 *
 * Drawn as a vertical falloff multiplied by a horizontal one, rather
 * than as a radial gradient, because a radial on a 30:1 plane just
 * makes an ellipse.
 */
export function bladeTex() {
  if (_blade !== undefined) return _blade;
  _blade = makeTex(128, (g, s) => {
    const across = g.createLinearGradient(0, 0, s, 0);
    across.addColorStop(0.00, "rgba(255,255,255,0)");
    across.addColorStop(0.18, "rgba(255,255,255,0.55)");
    across.addColorStop(0.50, "rgba(255,255,255,1)");
    across.addColorStop(0.82, "rgba(255,255,255,0.55)");
    across.addColorStop(1.00, "rgba(255,255,255,0)");
    g.fillStyle = across;
    g.fillRect(0, 0, s, s);
    // Multiply the vertical falloff on top so the edges go soft too.
    g.globalCompositeOperation = "destination-in";
    const down = g.createLinearGradient(0, 0, 0, s);
    down.addColorStop(0.00, "rgba(255,255,255,0)");
    down.addColorStop(0.42, "rgba(255,255,255,0.9)");
    down.addColorStop(0.50, "rgba(255,255,255,1)");
    down.addColorStop(0.58, "rgba(255,255,255,0.9)");
    down.addColorStop(1.00, "rgba(255,255,255,0)");
    g.fillStyle = down;
    g.fillRect(0, 0, s, s);
  });
  return _blade;
}

/**
 * THE RING — a soft-shouldered annulus, for shockwaves and halos.
 *
 * `ringGeometry` gives a mathematically perfect ring with two hard
 * edges, which at the sizes a shock reaches reads as a wireframe hoop.
 * This is the same shape with the edges bled off, mapped onto a plane —
 * so a shockwave has a bright leading edge and a soft trailing one,
 * which is what a pressure front actually looks like.
 *
 * The stops are deliberately asymmetric: tighter on the outside (0.78 →
 * 0.92) than the inside (0.55 → 0.78). The front of the wave is the
 * hard part; the back is the part that dissipates.
 */
export function ringTex() {
  if (_ring !== undefined) return _ring;
  _ring = makeTex(256, (g, s) => {
    const r = s / 2;
    const grad = g.createRadialGradient(r, r, 0, r, r, r);
    grad.addColorStop(0.00, "rgba(255,255,255,0)");
    grad.addColorStop(0.55, "rgba(255,255,255,0)");
    grad.addColorStop(0.72, "rgba(255,255,255,0.30)");
    grad.addColorStop(0.86, "rgba(255,255,255,1)");
    grad.addColorStop(0.93, "rgba(255,255,255,0.45)");
    grad.addColorStop(1.00, "rgba(255,255,255,0)");
    g.fillStyle = grad;
    g.fillRect(0, 0, s, s);
  });
  return _ring;
}

// ═══════════════════════════════════════════════════════════════════
// THE HAIRLINE PROBLEM (H24 pass 4)
// ═══════════════════════════════════════════════════════════════════
// `THREE.LineBasicMaterial.linewidth` IS IGNORED on every platform that
// matters. The WebGL spec allows a driver to support widths above 1px
// and essentially none of them do, so every `THREE.Line` in this file
// draws as a ONE PIXEL hairline no matter what the material says.
//
// That is not a small cosmetic issue here, because the line primitives
// are not niche — they are the most-used shapes in the whole library:
//
//     rune  111 cues     spiral 26     crack 21
//     chain  14 cues     bolt   10     constellation 8
//
// Every Casting in the game opens on a rune. Every Tome, all 72. And
// all of them have been drawing as a thread — visible, correct, and
// weightless, on a board where everything else got a soft falloff in
// pass 1. Next to a bloomed shockwave a 1px circle reads as a UI
// overlay rather than as light.
//
// ── THE FIX, AND WHY IT IS ALMOST FREE ─────────────────────────────
// The usual answer is a MeshLine — rebuild the path as a billboarded
// quad strip. That is real work per primitive, six times over, and it
// doubles the per-frame math on shapes that already animate their own
// vertices.
//
// This does something cheaper and, at these sizes, better looking:
// mount a `<points>` cloud that SHARES THE LINE'S OWN BufferGeometry.
// The line already writes its vertices every frame; the points object
// reads the same buffer, so the glow costs **one extra draw call and
// zero extra computation** and can never drift out of alignment with
// the line it is thickening — they are literally the same numbers.
//
// With the spark texture on it, each vertex becomes a soft dot sitting
// exactly on the path. The line stays as the crisp core; the points
// are the halo around it. Same trick as the sheath on Beam, applied to
// geometry that cannot be sheathed.
//
// `n` on the material is deliberately generous — these read as glow,
// not as beads, so they want to be bigger and dimmer than a normal
// particle.

/**
 * A soft halo riding a line's own vertices. One extra draw call, zero
 * extra math, and it cannot drift — it IS the line's buffer.
 *
 * ⚠ The geometry is passed as a PROP, not built as a JSX child, so R3F
 * does not own it and will not dispose it. That is deliberate and it is
 * the only correct arrangement here: the line already handed that same
 * geometry to `useDispose`, and a second disposal of one buffer is a
 * use-after-free waiting for a bloody duel to find it.
 *
 * Opacity is driven imperatively by the owning primitive each frame, so
 * the halo fades exactly with its line rather than on a clock of its
 * own. Starts at 0 so it can never flash on the frame before it is due.
 */
function LineGlow({ geo, color, size = 0.09, refObj }) {
  return (
    // `dispose={null}` declares OWNERSHIP, and the honest version of
    // this note is that it is not preventing a crash. `BufferGeometry
    // .dispose()` only dispatches an event the renderer uses to drop
    // its cache entry, so calling it twice is harmless — which is why
    // the H23 primitives above have been passing `geometry={geo}` to a
    // mesh AND to `useDispose` since July with no ill effect.
    //
    // It is here because this is the first place in the file where one
    // buffer is shared by two SEPARATE objects, and "the line owns this,
    // the halo only borrows it" is worth saying out loud to whoever
    // touches it next. Cheap, correct, and it documents the seam.
    <points ref={refObj} geometry={geo} dispose={null}>
      <pointsMaterial
        size={size} color={color} map={sparkTex()} transparent opacity={0}
        depthWrite={false} toneMapped={false}
        blending={THREE.AdditiveBlending} sizeAttenuation />
    </points>
  );
}

// ── RING_FIT ───────────────────────────────────────────────────────
// Where the ring texture's bright band sits, relative to where the old
// `ringGeometry` put its own. Every call site that swapped geometry for
// the texture and wants to keep its previously-tuned radius multiplies
// by this. See the long note in Shock, which is the one that mattered.
const RING_FIT = 0.70 / 0.86;

/** Points material props, with the soft dot already on. */
const sparkMat = (color, size) => ({
  size, color, map: sparkTex(), transparent: true, opacity: 0,
  depthWrite: false, toneMapped: false,
  blending: THREE.AdditiveBlending, sizeAttenuation: true,
});

// ═══════════════════════════════════════════════════════════════════
// PART II — THE THIRTEEN
// ═══════════════════════════════════════════════════════════════════

// ── 1 · BEAM — a line of force, A to B ─────────────────────────────
// The most honest primitive in the file: the attacker and the victim
// are connected by a thing, and for a moment you can see the thing. A
// cylinder is built once along +Y and then aimed with lookAt, which is
// both cheaper and more stable than rebuilding geometry per frame.
//
// The taper is what sells it: the beam SNAPS to full width in the
// first 18% and bleeds out over the rest (E.flick). A beam that fades
// in symmetrically reads as a glow stick; a beam that arrives already
// at full brightness reads as a shot.
function Beam({ cue, onDone }) {
  const grp = useRef();
  const mesh = useRef();
  const halo2 = useRef();
  const st = useRef(newClock());
  const geo = useMemo(() => {
    const g = new THREE.CylinderGeometry(1, 1, 1, 10, 1, true);
    g.translate(0, 0.5, 0); // origin at the base — we scale toward the target
    return g;
  }, []);
  useDispose(geo);
  const len = useMemo(() => V(cue.from).distanceTo(V(cue.to)), [cue.from, cue.to]);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!grp.current) return;
    if (t < 0) { grp.current.visible = false; return; }
    grp.current.visible = t < 1;
    if (t >= 1) return;
    const k = E.flick(t);
    // The head races out on the first beat, then the whole shaft dims.
    const reach = t < 0.18 ? E.snap(t / 0.18) : 1;
    const w = (cue.size || 0.05) * (0.35 + k * 0.9);
    if (mesh.current) {
      mesh.current.scale.set(w, len * reach, w);
      mesh.current.material.opacity = k;
    }
    // ── H24 · THE SHEATH ─────────────────────────────────────────
    // A single hard-edged cylinder is a PLASTIC ROD. Real light has a
    // hot core and a wide dim halo around it, and the cheapest honest
    // way to say that is a second cylinder — 3.4x wider, a fifth of
    // the opacity, additive so it sums with the core rather than
    // occluding it. Two draw calls instead of one, and it is the whole
    // difference between "a beam" and "a tube."
    //
    // It also lags the core slightly (k * 0.82 on the reach) so the
    // halo blooms out behind the shot instead of arriving with it.
    if (halo2.current) {
      const hw = w * 3.4;
      halo2.current.scale.set(hw, len * reach * 0.98, hw);
      halo2.current.material.opacity = k * 0.2;
    }
  });
  const quat = useMemo(() => {
    const dir = V(cue.to).sub(V(cue.from)).normalize();
    const q = new THREE.Quaternion();
    q.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    return q;
  }, [cue.from, cue.to]);
  return (
    <group ref={grp} position={cue.from} quaternion={quat} visible={false}>
      <mesh ref={halo2} geometry={geo}>
        <meshBasicMaterial {...glowMat(cue.color, 0)} />
      </mesh>
      <mesh ref={mesh} geometry={geo}>
        <meshBasicMaterial {...glowMat(cue.color, 0)} />
      </mesh>
    </group>
  );
}

// ── 2 · ORB — a projectile that crosses the seam ───────────────────
// The one primitive with real travel time, and the reason H-09's
// volley reads as a VOLLEY: three orbs at 90ms apart is a burst of
// fire, and no amount of pose could ever say that.
//
// The trail is four ghost spheres strung behind the head at fixed
// fractions of the path — cheaper than a real trail renderer, and at
// the speeds these move it is indistinguishable.
const ORB_TAIL = [0.05, 0.10, 0.16, 0.23];
function Orb({ cue, onDone }) {
  const grp = useRef();
  const head = useRef();
  const aura = useRef();
  const tail = useRef([]);
  const st = useRef(newClock());
  const { camera } = useThree();
  const arc = cue.r ?? 0.55;
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!grp.current) return;
    if (t < 0) { grp.current.visible = false; return; }
    grp.current.visible = t < 1;
    if (t >= 1) return;
    const place = (obj, k, scale) => {
      if (!obj) return;
      const kk = Math.max(0, Math.min(1, k));
      const p = lerp3(cue.from, cue.to, kk);
      obj.position.set(p[0], p[1] + Math.sin(kk * Math.PI) * arc, p[2]);
      obj.scale.setScalar(scale);
    };
    // The head eases OUT (fast off the hand, settling into the target)
    // — a projectile that accelerates into its victim looks thrown by
    // a machine; one that decelerates looks aimed.
    const k = E.out(t);
    place(head.current, k, (cue.size || 0.13) * (1 + (1 - t) * 0.25));
    if (head.current) head.current.material.opacity = t > 0.86 ? (1 - t) / 0.14 : 1;
    if (aura.current) {
      place(aura.current, k, (cue.size || 0.13) * 3.6);
      aura.current.lookAt(camera.position);
      aura.current.material.opacity = (t > 0.86 ? (1 - t) / 0.14 : 1) * 0.45;
    }
    tail.current.forEach((m, i) => {
      place(m, k - ORB_TAIL[i], (cue.size || 0.13) * (0.7 - i * 0.13));
      if (m) m.material.opacity = (0.55 - i * 0.11) * (t > 0.86 ? (1 - t) / 0.14 : 1);
    });
  });
  return (
    <group ref={grp} visible={false}>
      {/* The head is a small solid core INSIDE a much larger soft
          billboard. A bare additive sphere reads as a marble — you can
          see its silhouette, and a silhouette is the one thing light
          does not have. The glow sprite is 3.6x the core and carries
          most of the visible mass. */}
      <mesh ref={aura}>
        <planeGeometry args={[2, 2]} />
        <meshBasicMaterial {...glowMat(cue.color, 0)} map={glowTex()} />
      </mesh>
      <mesh ref={head}>
        <sphereGeometry args={[1, 12, 10]} />
        <meshBasicMaterial {...glowMat(cue.color, 1)} />
      </mesh>
      {ORB_TAIL.map((_, i) => (
        <mesh key={i} ref={(m) => { tail.current[i] = m; }}>
          <sphereGeometry args={[1, 8, 6]} />
          <meshBasicMaterial {...glowMat(cue.color, 0.4)} />
        </mesh>
      ))}
    </group>
  );
}

// ── 3 · SLASH — a claw-mark opens across the victim ────────────────
// THE most important primitive in the file, because it is the one that
// makes a wound READ as a wound. A crescent of ring geometry, swept
// open along its own arc: the cut is drawn, held for a breath, then
// bleeds out. `n` slashes fan at `tone` degrees apart with a 60ms
// stagger, which is the entire difference between a bite (n=2, tight)
// and a rake (n=3, wide).
//
// Billboarded — it faces the camera every frame — because a slash lying
// flat on the felt is invisible from the TABLE preset, and the TABLE
// preset is where most of a duel is watched from.
function Slash({ cue, onDone }) {
  const grp = useRef();
  const marks = useRef([]);
  const bleeds = useRef([]);
  const st = useRef(newClock());
  const { camera } = useThree();
  const n = Math.max(1, cue.n || 3);
  const spread = (cue.tone ?? 22) * (Math.PI / 180);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!grp.current) return;
    if (t < 0) { grp.current.visible = false; return; }
    grp.current.visible = t < 1;
    if (t >= 1) return;
    grp.current.lookAt(camera.position);
    marks.current.forEach((m, i) => {
      if (!m) return;
      // Each mark opens 60ms after the last — the stagger is the claw.
      const lag = i * (0.16 / n);
      const k = Math.max(0, Math.min(1, (t - lag) / (1 - lag)));
      const draw = k < 0.3 ? k / 0.3 : 1;          // the cut is drawn
      const fade = k < 0.45 ? 1 : 1 - (k - 0.45) / 0.55; // then it bleeds out
      m.scale.set((cue.r || 0.9) * draw, (cue.r || 0.9) * draw, 1);
      m.material.opacity = fade * 0.95;
      m.rotation.z = -spread * (i - (n - 1) / 2) + (1 - draw) * 0.25;
      // ── H24 · THE BLEED ────────────────────────────────────────
      // A wider, dimmer arc sitting behind the cut. A wound is not a
      // clean bright line — it is a bright line with something angry
      // around it, and a single crisp crescent reads as a decal no
      // matter how well it is timed.
      //
      // It LINGERS: the bleed's fade runs on a longer tail than the
      // cut's, so the mark itself goes first and the glow around it
      // stays a beat. That order is the whole reason this reads as a
      // wound closing rather than as two shapes fading together.
      const b = bleeds.current[i];
      if (b) {
        const bs = (cue.r || 0.9) * draw * 1.22;
        b.scale.set(bs, bs, 1);
        b.rotation.z = m.rotation.z;
        b.material.opacity = (k < 0.5 ? k * 1.4 : 1 - (k - 0.5) / 0.5) * 0.3;
      }
    });
  });
  return (
    <group ref={grp} position={cue.to || cue.from} visible={false}>
      {/* The bleed layer, behind every cut. Wider arc, low opacity,
          longer tail. */}
      {Array.from({ length: n }).map((_, i) => (
        <mesh key={`b${i}`} ref={(m) => { bleeds.current[i] = m; }}>
          <ringGeometry args={[0.56, 0.98, 22, 1, 2.24, 1.14]} />
          <meshBasicMaterial {...glowMat(cue.color, 0)} />
        </mesh>
      ))}
      {Array.from({ length: n }).map((_, i) => (
        <mesh key={i} ref={(m) => { marks.current[i] = m; }}>
          {/* A thin arc, not a full ring: thetaStart 2.35 rad and a
              0.9 rad sweep is the shape of something dragged. */}
          <ringGeometry args={[0.72, 0.86, 26, 1, 2.35, 0.9]} />
          <meshBasicMaterial {...glowMat(cue.color, 0)} />
        </mesh>
      ))}
    </group>
  );
}

// ── 4 · SHOCK — a pressure ring on the felt ────────────────────────
// The punctuation mark of the whole vocabulary. Almost every chord
// ends on one, because a ring on the ground is how the FIELD says it
// felt something — the difference between a card that glowed and a
// board that was hit.
function Shock({ cue, onDone }) {
  const ref = useRef();
  const st = useRef(newClock());
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!ref.current) return;
    if (t < 0) { ref.current.visible = false; return; }
    ref.current.visible = t < 1;
    if (t >= 1) return;
    const k = E.out(t);
    const r = (cue.size || 0.2) + ((cue.r || 1.6) - (cue.size || 0.2)) * k;
    // ⚠ RING_FIT — do not delete, and do not "simplify" to 1.0.
    //
    // The old `ringGeometry args={[0.62, 0.78, 56]}` put its bright
    // band at ~0.70 of its own scale, so a score asking for r:1.6 drew
    // a ring of true radius ~1.12. Every `r` in all 227 scores was
    // tuned by eye against THAT. The soft ring texture peaks at 0.86
    // instead, which silently inflated every shockwave in the game by
    // 23% the moment the falloff pass landed.
    //
    // 0.70/0.86 restores the exact radius caP'n has already signed off
    // on, so this pass buys a soft edge and changes nothing else. The
    // declared `r` is still not the literal world radius — it never
    // was — and making it literal is a re-tune of 227 scores, which is
    // a composition change and emphatically not what a crispness pass
    // is allowed to do on its own initiative.
    const fit = r * RING_FIT;
    ref.current.scale.set(fit, fit, 1);
    ref.current.material.opacity = (1 - t) * (cue.tone ?? 0.8);
  });
  const pos = cue.to || cue.from || [0, 0, 0];
  return (
    <mesh ref={ref} rotation={[-Math.PI / 2, 0, 0]}
      position={[pos[0], Y.seam + 0.006, pos[2]]} visible={false}>
      {/* THE PRESSURE FRONT. `ringGeometry` draws a perfect annulus
          with two hard edges — at the radius a shock reaches that is a
          wireframe hoop, not a wave. The ring texture bleeds both
          shoulders and is deliberately asymmetric: hard on the leading
          edge, soft on the trailing one, which is what a front
          actually looks like. Plane is 2x2 so `scale = radius` still
          means exactly what it meant before. */}
      <planeGeometry args={[2, 2]} />
      <meshBasicMaterial {...glowMat(cue.color, 0)} map={ringTex()} />
    </mesh>
  );
}

// ── 5 · MOTES — a scatter, outward or inward ───────────────────────
// `tone: "in"` converges instead of bursting, and that single flag is
// the whole difference between a Phantom dissolving (out) and Shadow
// Fox's nine shadows arriving from everywhere at once (in). Points,
// not meshes: fifty motes is one draw call.
function Motes({ cue, onDone }) {
  const pts = useRef();
  const st = useRef(newClock());
  const n = cue.n || 26;
  const R = cue.r || 1.1;
  const inward = cue.tone === "in";
  const { geo, dirs, arr } = useMemo(() => {
    const rr = rnd(cue.id || "motes");
    const d = [];
    for (let i = 0; i < n; i++) {
      const a = rr() * Math.PI * 2;
      const p = Math.acos(2 * rr() - 1);
      const mag = 0.35 + rr() * 0.65;
      d.push([
        Math.sin(p) * Math.cos(a) * mag,
        Math.abs(Math.cos(p)) * mag * 0.8 + 0.1,
        Math.sin(p) * Math.sin(a) * mag,
      ]);
    }
    const a2 = new Float32Array(n * 3);
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(a2, 3));
    return { geo: g, dirs: d, arr: a2 };
  }, [cue.id, n]);
  useDispose(geo);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!pts.current) return;
    if (t < 0) { pts.current.visible = false; return; }
    pts.current.visible = t < 1;
    if (t >= 1) return;
    const k = inward ? 1 - E.out(t) : E.out(t);
    for (let i = 0; i < n; i++) {
      arr[i * 3] = dirs[i][0] * R * k;
      arr[i * 3 + 1] = dirs[i][1] * R * k * 0.9;
      arr[i * 3 + 2] = dirs[i][2] * R * k;
    }
    geo.attributes.position.needsUpdate = true;
    pts.current.material.opacity = inward ? Math.min(1, t * 2.4) * (1 - t * 0.35) : 1 - t;
  });
  return (
    <points ref={pts} geometry={geo} position={cue.to || cue.from} visible={false}>
      <pointsMaterial {...sparkMat(cue.color, cue.size || 0.075)} />
    </points>
  );
}

// ── 6 · PILLAR — a column of light from the sky ────────────────────
// Judgment, starfall, descent. Comes DOWN, which is why it belongs to
// H-01 (the star that falls on you) and DG-0 (the verdict that lands).
// Slams to full height in the first fifth and then simply stands there
// dimming: a pillar that grows slowly reads as a special effect, one
// that is already there reads as something that arrived.
// ⚠ THE ORIGIN MATTERS MORE THAN THE CURVE HERE. A cylinder's origin
// is its MIDDLE, so scaling Y on the default geometry grows a column
// outward from its own waist — which reads as a special effect
// inflating, not as light arriving. The geometry is therefore
// translated so its origin sits at the TOP, the mesh is parked at
// sky height, and scale.y grows the column DOWNWARD onto the felt.
// One translate, and the primitive means what its name says.
function Pillar({ cue, onDone }) {
  const ref = useRef();
  const sheath = useRef();
  const st = useRef(newClock());
  const h = cue.r || 5.2;
  const geo = useMemo(() => {
    const g = new THREE.CylinderGeometry(1, 0.72, h, 20, 1, true);
    g.translate(0, -h / 2, 0);
    return g;
  }, [h]);
  useDispose(geo);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!ref.current) return;
    if (t < 0) { ref.current.visible = false; if (sheath.current) sheath.current.visible = false; return; }
    ref.current.visible = t < 1;
    if (sheath.current) sheath.current.visible = t < 1;
    if (t >= 1) return;
    const drop = t < 0.2 ? E.snap(t / 0.2) : 1;
    const w = (cue.size || 0.5) * (0.6 + drop * 0.4) * (1 + (1 - drop) * 0.5);
    ref.current.scale.set(w, drop, w);
    ref.current.material.opacity = drop * (1 - E.in2(t)) * 0.9;
    ref.current.rotation.y += dt * (cue.spin || 0.4);
    // The sheath, same reasoning as Beam's — and it counter-rotates,
    // which is what stops a spinning cylinder from reading as a solid
    // object with a texture on it. Two shells turning opposite ways
    // read as volume; one reads as a barber pole.
    if (sheath.current) {
      const sw = w * 2.1;
      sheath.current.scale.set(sw, drop * 1.02, sw);
      sheath.current.material.opacity = drop * (1 - E.in2(t)) * 0.22;
      sheath.current.rotation.y -= dt * (cue.spin || 0.4) * 0.6;
    }
  });
  const pos = cue.to || cue.from || [0, 0, 0];
  return (
    <group position={[pos[0], Y.seam + 0.01 + h, pos[2]]}>
      <mesh ref={sheath} geometry={geo} visible={false}>
        <meshBasicMaterial {...glowMat(cue.color, 0)} />
      </mesh>
      <mesh ref={ref} geometry={geo} visible={false}>
        <meshBasicMaterial {...glowMat(cue.color, 0)} />
      </mesh>
    </group>
  );
}

// ── 7 · SPIRAL — a ribbon that winds around something ──────────────
// Constriction (Inverted Serpent) and eruption (Chaos Dragon) are the
// same motion at different angles, which is a nice piece of luck: one
// primitive, `tone: "up"` for the column, default for the coil.
const SPIRAL_SEG = 40;
function Spiral({ cue, onDone }) {
  const line = useRef();
  const glow = useRef();
  const st = useRef(newClock());
  const turns = cue.spin || 2;
  const R = cue.r || 0.75;
  const up = cue.tone === "up";
  const obj = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(SPIRAL_SEG * 3), 3));
    const m = new THREE.LineBasicMaterial({
      color: cue.color, transparent: true, opacity: 0,
      depthWrite: false, toneMapped: false, blending: THREE.AdditiveBlending,
    });
    const L = new THREE.Line(g, m);
    L.visible = false; // never flash on the frame before it is due
    return L;
  }, [cue.color]);
  useDispose(obj);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!obj) return;
    if (t < 0) { obj.visible = false; return; }
    obj.visible = t < 1;
    if (t >= 1) return;
    const grow = Math.min(1, t / 0.55);
    const squeeze = up ? 1 : 1 - Math.max(0, (t - 0.55) / 0.45) * 0.45;
    const arr = obj.geometry.attributes.position.array;
    for (let i = 0; i < SPIRAL_SEG; i++) {
      const f = (i / (SPIRAL_SEG - 1)) * grow;
      const a = f * Math.PI * 2 * turns + t * 4.2;
      const rad = R * squeeze * (up ? 0.45 + f * 0.8 : 1);
      arr[i * 3] = Math.cos(a) * rad;
      arr[i * 3 + 1] = up ? f * (cue.size || 2.4) : (f - 0.5) * (cue.size || 1.1);
      arr[i * 3 + 2] = Math.sin(a) * rad;
    }
    obj.geometry.attributes.position.needsUpdate = true;
    obj.geometry.setDrawRange(0, Math.max(2, Math.floor(SPIRAL_SEG * grow)));
    obj.material.opacity = (t < 0.7 ? 1 : 1 - (t - 0.7) / 0.3) * 0.95;
    // The halo rides the SAME buffer the line just wrote, so it can
    // never drift out of alignment and costs no extra math.
    if (glow.current) {
      glow.current.geometry.setDrawRange(0, Math.max(2, Math.floor(SPIRAL_SEG * grow)));
      glow.current.material.opacity = obj.material.opacity * 0.5;
    }
  });
  const pos = cue.to || cue.from || [0, 0, 0];
  return (
    <group position={[pos[0], pos[1] + 0.05, pos[2]]}>
      <primitive ref={line} object={obj} />
      <LineGlow geo={obj.geometry} color={cue.color} size={0.11} refObj={glow} />
    </group>
  );
}

// ── 8 · BOLT — a jagged tear between two points ────────────────────
// Not lightning as weather; lightning as VIOLENCE. Re-jittered every
// third frame so it crackles rather than slides, and drawn as one Line
// so a bolt costs a single draw call no matter how many kinks it has.
const BOLT_SEG = 13;
function Bolt({ cue, onDone }) {
  const ref = useRef();
  const glow = useRef();
  const st = useRef(newClock());
  const jitter = cue.r || 0.28;
  const obj = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(BOLT_SEG * 3), 3));
    const m = new THREE.LineBasicMaterial({
      color: cue.color, transparent: true, opacity: 0,
      depthWrite: false, toneMapped: false, blending: THREE.AdditiveBlending,
    });
    const L = new THREE.Line(g, m);
    L.visible = false;
    return L;
  }, [cue.color]);
  useDispose(obj);
  const frame = useRef(0);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!obj) return;
    if (t < 0) { obj.visible = false; return; }
    obj.visible = t < 1;
    if (t >= 1) return;
    frame.current += 1;
    if (frame.current % 3 === 0 || frame.current === 1) {
      const rr = rnd((cue.id || "bolt") + frame.current);
      const arr = obj.geometry.attributes.position.array;
      for (let i = 0; i < BOLT_SEG; i++) {
        const f = i / (BOLT_SEG - 1);
        const p = lerp3(cue.from, cue.to, f);
        // Zero deviation at both ends — a bolt must actually TOUCH the
        // two things it connects or it reads as ambient decoration.
        const w = Math.sin(f * Math.PI) * jitter;
        arr[i * 3] = p[0] + (rr() - 0.5) * w;
        arr[i * 3 + 1] = p[1] + (rr() - 0.5) * w + 0.05;
        arr[i * 3 + 2] = p[2] + (rr() - 0.5) * w;
      }
      obj.geometry.attributes.position.needsUpdate = true;
    }
    obj.material.opacity = E.flick(t);
    if (glow.current) glow.current.material.opacity = obj.material.opacity * 0.55;
  });
  return (
    <group>
      <primitive ref={ref} object={obj} />
      <LineGlow geo={obj.geometry} color={cue.color} size={0.13} refObj={glow} />
    </group>
  );
}

// ── 9 · WAVE — a front that rolls across the field ─────────────────
// The only primitive that is about the BOARD rather than a card. A
// sovereign decree, a pressure front, an edict crossing the seam. It
// travels from `from` toward `to` as a wide low arc, so it reads as a
// thing passing THROUGH the field rather than a thing centred on a
// victim.
function Wave({ cue, onDone }) {
  const ref = useRef();
  const st = useRef(newClock());
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!ref.current) return;
    if (t < 0) { ref.current.visible = false; return; }
    ref.current.visible = t < 1;
    if (t >= 1) return;
    const k = E.out(t);
    const p = lerp3(cue.from, cue.to, k);
    ref.current.position.set(p[0], Y.seam + 0.02 + Math.sin(t * Math.PI) * 0.1, p[2]);
    ref.current.scale.set(cue.r || 4.2, 1, 1);
    ref.current.material.opacity = E.bump(t) * (cue.tone ?? 0.75);
  });
  const dir = useMemo(() => {
    const d = V(cue.to).sub(V(cue.from));
    return Math.atan2(d.x, d.z);
  }, [cue.from, cue.to]);
  return (
    <mesh ref={ref} rotation={[-Math.PI / 2, 0, -dir]} visible={false}>
      {/* The blade texture along the front: bright at the centre of
          the sweep, gone at both wingtips. A hard-edged plane crossing
          the felt read as a sliding door. */}
      <planeGeometry args={[1, cue.size || 0.5]} />
      <meshBasicMaterial {...glowMat(cue.color, 0)} map={bladeTex()} />
    </mesh>
  );
}

// ── 10 · BLOOM — a soft flash at a point ───────────────────────────
// The cheapest cue in the file and the most used. Every impact wants
// one; it is the light a thing makes when it happens.
function Bloom({ cue, onDone }) {
  const ref = useRef();
  const st = useRef(newClock());
  const { camera } = useThree();
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!ref.current) return;
    if (t < 0) { ref.current.visible = false; return; }
    ref.current.visible = t < 1;
    if (t >= 1) return;
    ref.current.lookAt(camera.position);
    const k = E.flick(t);
    const s = (cue.r || 0.9) * (0.45 + E.out(t) * 0.85);
    ref.current.scale.set(s, s, 1);
    ref.current.material.opacity = k * (cue.tone ?? 0.85);
  });
  return (
    <mesh ref={ref} position={cue.to || cue.from} visible={false}>
      {/* A plane + the glow falloff, NOT a circleGeometry. A flat
          additive circle has a hard rim and reads as a coin of light;
          this is the same footprint with the edge bled off. Most-used
          primitive in the game, so this one line is the single biggest
          visual change in the pass. */}
      <planeGeometry args={[2, 2]} />
      <meshBasicMaterial {...glowMat(cue.color, 0)} map={glowTex()} />
    </mesh>
  );
}

// ── 11 · STREAM — a directed column of particles ───────────────────
// Motes have no destination; a stream is all destination. This is the
// swarm, the drain, the transfer — anything where a QUANTITY of small
// things crosses the gap in one direction. Each particle carries its
// own phase offset so the column is continuous rather than pulsed.
function Stream({ cue, onDone }) {
  const pts = useRef();
  const st = useRef(newClock());
  const n = cue.n || 40;
  const spread = cue.r || 0.22;
  const { geo, seeds, arr } = useMemo(() => {
    const rr = rnd(cue.id || "stream");
    const s = Array.from({ length: n }, () => ({
      ph: rr(), ox: (rr() - 0.5) * 2, oy: (rr() - 0.5) * 2, oz: (rr() - 0.5) * 2,
      sw: 3 + rr() * 5,
    }));
    const a = new Float32Array(n * 3);
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(a, 3));
    return { geo: g, seeds: s, arr: a };
  }, [cue.id, n]);
  useDispose(geo);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!pts.current) return;
    if (t < 0) { pts.current.visible = false; return; }
    pts.current.visible = t < 1;
    if (t >= 1) return;
    for (let i = 0; i < n; i++) {
      const s = seeds[i];
      const f = (t * 1.35 + s.ph) % 1;
      const p = lerp3(cue.from, cue.to, f);
      const w = Math.sin(f * Math.PI) * spread;
      arr[i * 3] = p[0] + s.ox * w + Math.sin(t * s.sw + s.ph * 9) * w * 0.4;
      arr[i * 3 + 1] = p[1] + s.oy * w * 0.7 + 0.08;
      arr[i * 3 + 2] = p[2] + s.oz * w;
    }
    geo.attributes.position.needsUpdate = true;
    pts.current.material.opacity = t < 0.75 ? 1 : 1 - (t - 0.75) / 0.25;
  });
  return (
    <points ref={pts} geometry={geo} visible={false}>
      <pointsMaterial {...sparkMat(cue.color, cue.size || 0.06)} />
    </points>
  );
}

// ── 12 · GLYPH — a mark, spoken over the board ─────────────────────
// DOM, exactly like H21's ArrivalSigil, because typography is the one
// thing WebGL is worse at than a browser. Rises, spins if asked, and
// fades. The Wager's die and the Verdict's seal are both this.
function Glyph({ cue, onDone }) {
  const st = useRef(newClock());
  const box = useRef();
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    const el = box.current;
    if (!el) return;
    if (t < 0) { el.style.opacity = "0"; return; }
    if (t >= 1) { el.style.opacity = "0"; return; }
    const rise = E.out(t) * (cue.r ?? 0.6);
    const spin = (cue.spin || 0) * t * 360;
    const s = 0.7 + E.out(Math.min(1, t / 0.35)) * 0.55 - t * 0.15;
    el.style.transform = `translateY(${-rise * 46}px) rotate(${spin}deg) scale(${s})`;
    el.style.opacity = String(t < 0.65 ? Math.min(1, t * 5) : (1 - t) / 0.35);
  });
  const pos = cue.to || cue.from || [0, 0, 0];
  return (
    <Html position={[pos[0], pos[1] + 0.5, pos[2]]} center
      zIndexRange={[29, 20]} style={{ pointerEvents: "none" }}>
      <div ref={box} style={{
        opacity: 0, fontFamily: GM.font, fontWeight: 900,
        fontSize: cue.size || 40, color: cue.color, lineHeight: 1,
        textShadow: `0 0 16px ${cue.color}, 0 0 34px ${cue.color}99, 0 2px 5px rgba(0,0,0,0.95)`,
        whiteSpace: "nowrap", willChange: "transform, opacity",
      }}>{cue.tone || "✦"}</div>
    </Html>
  );
}

// ── 13 · GRAVE — the ground gives something back ───────────────────
// caP'n, on Resurrection: "the most basic level of Resurrection will be
// the classic skulls etc graveyard looking animation... the variations
// should be basically the same just maybe slightly different colors or
// way different and no skulls put something else (sometimes)."
//
// So this is ONE primitive with a swappable mark. Marks rise out of the
// felt in a ring, drift up, and fade — skulls by default, and any glyph
// the caller hands it otherwise. That is the whole variation surface,
// and it is deliberately the whole variation surface: twelve bespoke
// resurrections would have been twelve times the work for a moment that
// is already saying exactly what it needs to say.
// ⚠ ONE PORTAL, NOT SEVEN. The obvious build is a <Html> per mark,
// and it costs seven React portals plus seven per-frame matrix syncs
// for one resurrection. This mounts ONE billboard at the rift and
// scatters the marks inside it with plain CSS offsets — same picture,
// one seventh of the machinery, and the scatter now rides a single
// transform sync instead of racing seven.
function Grave({ cue, onDone }) {
  const st = useRef(newClock());
  const items = useRef([]);
  const n = Math.max(3, cue.n || 6);
  const marks = useMemo(() => {
    const rr = rnd(cue.id || "grave");
    return Array.from({ length: n }, (_, i) => ({
      // Elliptical scatter in SCREEN space — the felt is seen at a
      // rake from every camera preset, so a circle in x/y reads as the
      // ellipse a ring on the ground actually makes.
      dx: Math.cos((i / n) * Math.PI * 2 + rr() * 0.6) * (cue.r || 0.72) * 78,
      dy: Math.sin((i / n) * Math.PI * 2 + rr() * 0.6) * (cue.r || 0.72) * 34,
      lag: rr() * 0.35,
      rise: 0.55 + rr() * 0.6,
      tilt: (rr() - 0.5) * 40,
      size: (cue.size || 26) * (0.75 + rr() * 0.5),
    }));
  }, [cue.id, n, cue.r, cue.size]);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    for (let i = 0; i < items.current.length; i++) {
      const el = items.current[i];
      if (!el) continue;
      const m = marks[i];
      if (t < 0) { el.style.opacity = "0"; continue; }
      const k = Math.max(0, Math.min(1, (t - m.lag) / (1 - m.lag)));
      const up = E.out(k) * m.rise;
      el.style.transform =
        `translate(${m.dx}px, ${m.dy - up * 52}px) rotate(${m.tilt * (1 - k)}deg) scale(${0.6 + k * 0.55})`;
      el.style.opacity = String(k < 0.25 ? k * 4 : Math.max(0, 1 - (k - 0.25) / 0.75));
    }
  });
  const pos = cue.to || cue.from || [0, 0, 0];
  return (
    <Html position={[pos[0], pos[1] + 0.12, pos[2]]} center
      zIndexRange={[28, 19]} style={{ pointerEvents: "none" }}>
      <div style={{ position: "relative", width: 0, height: 0 }}>
        {marks.map((m, i) => (
          <div key={i} ref={(el) => { items.current[i] = el; }} style={{
            position: "absolute", left: 0, top: 0, opacity: 0,
            fontSize: m.size, lineHeight: 1, color: cue.color,
            textShadow: `0 0 14px ${cue.color}, 0 0 30px ${cue.color}88, 0 2px 4px rgba(0,0,0,0.9)`,
            willChange: "transform, opacity",
          }}>{cue.tone || "☠"}</div>
        ))}
      </div>
    </Html>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PART II.5 — THE FOURTEEN MORE · H24
// ═══════════════════════════════════════════════════════════════════
// caP'n (Aug 3): "update the entire shatterfield with super cool unique
// animations for all the cards, all the effects, all the interactions,
// everything that makes sense... a cool animation for every single
// Impera, U'hlti, action."
//
// ── WHY MORE PRIMITIVES BEFORE MORE SCORES ─────────────────────────
// The thirteen were sized for one species and the four Ilks. They are
// magnificent at violence and nearly mute at everything else: there is
// no primitive in the original set that means SEARCH, or BIND, or
// NULLIFY, or BUILD, or GAMBLE — and those five verbs alone cover 45 of
// the 72 Tomes. Authoring 180 scores on a vocabulary that can only say
// "hit" produces 180 scores that all read as hits.
//
// So the vocabulary grows FIRST, and it grows along the axes the card
// database actually uses:
//
//   the eight Tome schools     → rune · lattice · veil · shatter ·
//                                vortex · embers · coin · chain
//   the four Affinities        → halo
//   the Space / Time signature → rift · echoes · star
//   ruin, doom and execution   → crack · sever
//
// Twenty-seven primitives total. That is the ceiling coming off: every
// score from here down is a CHORD, never new engine code, which is the
// whole point of the Exception Law's "one more data field, not one more
// subsystem." A card minted next year picks four of these and is done.
//
// ── EVERY LAW ABOVE STILL BINDS, NONE RELAXED ──────────────────────
// Geometry in a useMemo, handed to useDispose. Animation imperative on
// refs inside useFrame. Zero setState. Zero Math.random — every scatter
// is seeded off the cue id so both clients draw the identical pattern.
// A cue that is not yet due is mounted and INVISIBLE. Every primitive
// retires itself through cueTick's onDone exactly once.
//
// ── THE INSTANCING NOTE ────────────────────────────────────────────
// Two of these (shatter, lattice) would naively be N meshes. At the
// counts the scores actually use — 14 shards, 12 edges — that is 14 and
// 12 draw calls for one cue, and a Solomon Godfry resolving four of
// them at once is 100+ draws in a frame that also has 20 cards and a
// holo pass in it. Both are therefore InstancedMesh: one geometry, one
// material, one draw call, matrices written straight into the buffer.
// ═══════════════════════════════════════════════════════════════════

// ── 14 · RUNE — a circle of power draws itself on the felt ─────────
// The Tome primitive. Spellwork in Terror is a PAGE OPENING, and the
// visual grammar of that everywhere is a ring inscribing itself: two
// concentric circles drawn in opposite directions, tick marks around
// the rim, and a hold before it lets go. Drawn as Lines with an
// animated drawRange, which is what makes it INSCRIBE rather than
// fade in — the difference between a spell being cast and a decal
// being placed.
//
// `n`     tick marks around the rim (0 = a clean ring)
// `spin`  revolutions the whole figure turns over its life
// `tone`  "close" to run the inscription backward — a seal SHUTTING,
//         which is what a Nihil ward and an Affinity both are.
const RUNE_SEG = 72;
function Rune({ cue, onDone }) {
  const grp = useRef();
  const gOut = useRef();
  const gIn = useRef();
  const st = useRef(newClock());
  const R = cue.r || 1.0;
  const ticks = cue.n ?? 8;
  const closing = cue.tone === "close";
  const { outer, inner, marks } = useMemo(() => {
    const mk = (rad, segs) => {
      const g = new THREE.BufferGeometry();
      const a = new Float32Array(segs * 3);
      for (let i = 0; i < segs; i++) {
        const th = (i / (segs - 1)) * Math.PI * 2;
        a[i * 3] = Math.cos(th) * rad;
        a[i * 3 + 1] = 0;
        a[i * 3 + 2] = Math.sin(th) * rad;
      }
      g.setAttribute("position", new THREE.BufferAttribute(a, 3));
      const m = new THREE.LineBasicMaterial({
        color: cue.color, transparent: true, opacity: 0,
        depthWrite: false, toneMapped: false, blending: THREE.AdditiveBlending,
      });
      const L = new THREE.Line(g, m);
      L.visible = false;
      return L;
    };
    // The tick marks are one LineSegments: 2 vertices per spoke, one
    // draw call for the whole rim rather than one per mark.
    const tg = new THREE.BufferGeometry();
    const ta = new Float32Array(Math.max(1, ticks) * 6);
    for (let i = 0; i < ticks; i++) {
      const th = (i / Math.max(1, ticks)) * Math.PI * 2;
      const c = Math.cos(th), s = Math.sin(th);
      ta[i * 6] = c * R * 1.02; ta[i * 6 + 1] = 0; ta[i * 6 + 2] = s * R * 1.02;
      ta[i * 6 + 3] = c * R * 1.20; ta[i * 6 + 4] = 0; ta[i * 6 + 5] = s * R * 1.20;
    }
    tg.setAttribute("position", new THREE.BufferAttribute(ta, 3));
    const tm = new THREE.LineBasicMaterial({
      color: cue.color, transparent: true, opacity: 0,
      depthWrite: false, toneMapped: false, blending: THREE.AdditiveBlending,
    });
    const T = new THREE.LineSegments(tg, tm);
    T.visible = false;
    return { outer: mk(R, RUNE_SEG), inner: mk(R * 0.62, RUNE_SEG), marks: T };
  }, [cue.color, R, ticks]);
  useDispose(outer, inner, marks);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!grp.current) return;
    if (t < 0) { grp.current.visible = false; return; }
    grp.current.visible = t < 1;
    if (t >= 1) return;
    // Inscribe over the first 55%, hold, release. Closing runs it in
    // reverse, so the ring is already whole and pulls shut.
    const raw = Math.min(1, t / 0.55);
    const draw = closing ? 1 - raw * 0.92 : raw;
    outer.geometry.setDrawRange(0, Math.max(2, Math.floor(RUNE_SEG * draw)));
    // The inner ring inscribes the OTHER way — a counter-drawn circle
    // is what stops this reading as a loading spinner.
    inner.geometry.setDrawRange(0, Math.max(2, Math.floor(RUNE_SEG * draw)));
    outer.rotation.y = (cue.spin || 0.5) * t * Math.PI * 2;
    inner.rotation.y = -(cue.spin || 0.5) * t * Math.PI * 2 * 1.6;
    marks.rotation.y = outer.rotation.y;
    const fade = t < 0.62 ? 1 : 1 - (t - 0.62) / 0.38;
    outer.material.opacity = fade;
    inner.material.opacity = fade * 0.75;
    marks.material.opacity = fade * 0.85 * (raw > 0.4 ? 1 : raw / 0.4);
    outer.visible = inner.visible = marks.visible = true;
    // 111 cues in the library open on a rune — this is the single
    // most-drawn shape in Terror, and it was a 1px thread.
    if (gOut.current) {
      gOut.current.geometry.setDrawRange(0, Math.max(2, Math.floor(RUNE_SEG * draw)));
      gOut.current.material.opacity = fade * 0.5;
      gOut.current.rotation.y = outer.rotation.y;
    }
    if (gIn.current) {
      gIn.current.geometry.setDrawRange(0, Math.max(2, Math.floor(RUNE_SEG * draw)));
      gIn.current.material.opacity = fade * 0.36;
      gIn.current.rotation.y = inner.rotation.y;
    }
    const puff = 1 + (1 - fade) * 0.16;
    grp.current.scale.set(puff, 1, puff);
  });
  const pos = cue.to || cue.from || [0, 0, 0];
  return (
    <group ref={grp} position={[pos[0], Y.seam + 0.008, pos[2]]} visible={false}>
      <primitive object={outer} />
      <primitive object={inner} />
      <primitive object={marks} />
      <LineGlow geo={outer.geometry} color={cue.color} size={0.1} refObj={gOut} />
      <LineGlow geo={inner.geometry} color={cue.color} size={0.085} refObj={gIn} />
    </group>
  );
}

// ── 15 · VEIL — a dome closes over something ───────────────────────
// NIHIL, which is nine Tomes and one Affinity: "the effect is
// nullified; the fare stays paid." Nullification is not an explosion,
// it is a REFUSAL — so the veil snaps up hard (something arriving to
// say no), holds flat and bright, then thins out rather than bursting.
// The rim ring is what sells the dome as a solid volume instead of a
// blue smudge; without it the shape reads as fog.
function Veil({ cue, onDone }) {
  const grp = useRef();
  const dome = useRef();
  const rim = useRef();
  const st = useRef(newClock());
  const geo = useMemo(
    () => new THREE.SphereGeometry(1, 22, 12, 0, Math.PI * 2, 0, Math.PI * 0.5),
    []);
  useDispose(geo);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!grp.current) return;
    if (t < 0) { grp.current.visible = false; return; }
    grp.current.visible = t < 1;
    if (t >= 1) return;
    // Snap up in the first 18%, then hold. A dome that eases open is a
    // shield being offered; one that is already there is a shield.
    // E.back on the snap: the dome punches very slightly past its
    // radius and pulls back. A shield that arrives at exactly its final
    // size reads as a fade-in no matter how fast it is.
    const up = t < 0.18 ? E.back(t / 0.18) : 1;
    const R = (cue.r || 1.35) * (0.35 + up * 0.65);
    const fade = t < 0.5 ? 1 : 1 - (t - 0.5) / 0.5;
    if (dome.current) {
      dome.current.scale.set(R, R * (cue.size || 0.72), R);
      // The surface breathes very slightly while it holds — a perfectly
      // static dome for 600ms looks like a frozen frame.
      dome.current.material.opacity = fade * 0.34 * (0.85 + Math.sin(t * 22) * 0.15);
    }
    if (rim.current) {
      // ⚠ 1.16, not 1.0. The rim used to be a `ringGeometry` whose
      // outer edge sat at 0.95 of its own scale, so `scale = R` put it
      // exactly on the dome's base. The soft ring TEXTURE peaks at 0.86
      // of the plane's half-width instead, which quietly pulled the rim
      // 10% inside the dome it is supposed to be the edge OF — the
      // shield gained a visible seam floating within itself.
      //
      // This is the whole hazard of the falloff pass in one line:
      // swapping geometry for a texture moves where the bright part
      // actually IS, and every call site that assumed the old radius
      // has to be re-tuned rather than left alone.
      const rr = R * 1.16;
      rim.current.scale.set(rr, rr, 1);
      rim.current.material.opacity = fade * 0.95;
    }
  });
  const pos = cue.to || cue.from || [0, 0, 0];
  return (
    <group ref={grp} position={[pos[0], Y.seam + 0.01, pos[2]]} visible={false}>
      <mesh ref={dome} geometry={geo}>
        <meshBasicMaterial {...glowMat(cue.color, 0)} />
      </mesh>
      <mesh ref={rim} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.004, 0]}>
        <planeGeometry args={[2, 2]} />
        <meshBasicMaterial {...glowMat(cue.color, 0)} map={ringTex()} />
      </mesh>
    </group>
  );
}

// ── 16 · SHATTER — a thing comes apart ─────────────────────────────
// OMNIA, the other nine: "the card is DESTROYED, not merely stopped."
// The distinction between Nihil and Omnia is the whole spine of
// Terror's counter-play, so the two must never look alike — a veil
// closes, a shatter opens, and you can tell which one resolved from
// across the room. Shards launch on seeded directions, tumble, take
// gravity, and go out.
//
// One InstancedMesh. Fourteen shards is fourteen matrix writes and a
// single draw call.
function Shatter({ cue, onDone }) {
  const inst = useRef();
  const st = useRef(newClock());
  const n = Math.max(3, cue.n || 14);
  const R = cue.r || 1.5;
  const { geo, seeds, dummy } = useMemo(() => {
    const rr = rnd(cue.id || "shatter");
    // A flat triangle, not a box: shards of a broken SURFACE. The
    // asymmetry is deliberate — three equal shards read as confetti.
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array([
      0, 0, 0, 1, 0, 0.32, 0.45, 0, -0.8,
    ]), 3));
    g.computeVertexNormals();
    const s = Array.from({ length: n }, () => {
      const a = rr() * Math.PI * 2;
      const lift = 0.55 + rr() * 1.5;
      return {
        dx: Math.cos(a), dz: Math.sin(a),
        mag: 0.4 + rr() * 0.75, lift,
        spinX: (rr() - 0.5) * 15, spinY: (rr() - 0.5) * 15, spinZ: (rr() - 0.5) * 15,
        size: 0.16 + rr() * 0.3, lag: rr() * 0.16,
      };
    });
    return { geo: g, seeds: s, dummy: new THREE.Object3D() };
  }, [cue.id, n]);
  useDispose(geo);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!inst.current) return;
    if (t < 0) { inst.current.visible = false; return; }
    inst.current.visible = t < 1;
    if (t >= 1) return;
    for (let i = 0; i < n; i++) {
      const s = seeds[i];
      const k = Math.max(0, Math.min(1, (t - s.lag) / (1 - s.lag)));
      const out = E.out(k) * R * s.mag;
      // Ballistic: up fast, then gravity wins. The shards must come
      // back DOWN or the cue reads as a firework, and a ruin is not
      // celebratory.
      const y = s.lift * k * 0.9 - 3.1 * k * k * 0.55;
      dummy.position.set(s.dx * out, Math.max(-0.02, y) + 0.1, s.dz * out);
      // Tumble decays with the flight instead of spinning at a
      // constant rate to the last frame — debris slows as it loses
      // energy, and constant-rate rotation is the single most common
      // tell of a fake particle.
      const tumble = k * (1 - k * 0.3);
      dummy.rotation.set(s.spinX * tumble, s.spinY * tumble, s.spinZ * tumble);
      dummy.scale.setScalar(s.size * (1 - k * 0.45));
      dummy.updateMatrix();
      inst.current.setMatrixAt(i, dummy.matrix);
    }
    inst.current.instanceMatrix.needsUpdate = true;
    inst.current.material.opacity = t < 0.25 ? 1 : 1 - (t - 0.25) / 0.75;
  });
  const pos = cue.to || cue.from || [0, 0, 0];
  return (
    <instancedMesh ref={inst} args={[geo, undefined, n]}
      position={[pos[0], pos[1], pos[2]]} visible={false}>
      <meshBasicMaterial {...glowMat(cue.color, 0)} />
    </instancedMesh>
  );
}

// ── 17 · CHAIN — two things are bound to each other ────────────────
// CONTRACT, the eighth school: "a legally binding offer to your
// enemy." Every Contract Tome in the game does something to BOTH
// Warlocks, and no other primitive in the file can say "both" — beam
// and orb are directional, wave passes through. A chain has two ends
// and that is its entire meaning.
//
// It SAGS first, then snaps taut, which is the moment the deal closes.
// A chain drawn taut from frame one is a rope; a chain that pulls tight
// is an agreement.
const CHAIN_SEG = 26;
function Chain({ cue, onDone }) {
  const st = useRef(newClock());
  const glow = useRef();
  const links = useRef();
  const obj = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(CHAIN_SEG * 3), 3));
    const m = new THREE.LineBasicMaterial({
      color: cue.color, transparent: true, opacity: 0,
      depthWrite: false, toneMapped: false, blending: THREE.AdditiveBlending,
    });
    const L = new THREE.Line(g, m);
    L.visible = false;
    return L;
  }, [cue.color]);
  useDispose(obj);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!obj) return;
    if (t < 0) { obj.visible = false; return; }
    obj.visible = t < 1;
    if (t >= 1) return;
    // sag 1 → 0 over the first 45%: the slack coming out of the line.
    const sag = (cue.r ?? 0.9) * Math.max(0, 1 - E.snap(Math.min(1, t / 0.45)));
    const arr = obj.geometry.attributes.position.array;
    for (let i = 0; i < CHAIN_SEG; i++) {
      const f = i / (CHAIN_SEG - 1);
      const p = lerp3(cue.from, cue.to, f);
      arr[i * 3] = p[0];
      arr[i * 3 + 1] = p[1] + 0.06 - Math.sin(f * Math.PI) * sag;
      arr[i * 3 + 2] = p[2];
    }
    obj.geometry.attributes.position.needsUpdate = true;
    // The snap is a brightness event, not a motion one: the line goes
    // hot at the instant the slack hits zero.
    const hot = t > 0.4 && t < 0.56 ? 1 : 0.62;
    obj.material.opacity = (t < 0.72 ? 1 : 1 - (t - 0.72) / 0.28) * hot;
    if (links.current) {
      links.current.material.opacity = obj.material.opacity * 0.9;
      links.current.material.size = (cue.size || 0.09) * (1 + (1 - sag) * 0.3);
    }
    // ⚠ THE BEADS BELOW ARE STATIC — they were built once from the
    // straight from→to path and never move, so while the chain SAGS
    // they float off it and only line up once it snaps taut. This glow
    // rides the live buffer and is correct the whole way through; the
    // beads stay because their chunky, evenly-spaced read is what says
    // "chain" rather than "laser," and they are right at the moment
    // that matters most.
    if (glow.current) glow.current.material.opacity = obj.material.opacity * 0.45;
  });
  // Link beads ride the same path as points — cheap, and they give the
  // line the chunky read of a chain instead of a laser.
  const beadGeo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const a = new Float32Array(9 * 3);
    for (let i = 0; i < 9; i++) {
      const p = lerp3(cue.from, cue.to, i / 8);
      a[i * 3] = p[0]; a[i * 3 + 1] = p[1] + 0.06; a[i * 3 + 2] = p[2];
    }
    g.setAttribute("position", new THREE.BufferAttribute(a, 3));
    return g;
  }, [cue.from, cue.to]);
  useDispose(beadGeo);
  return (
    <>
      <primitive object={obj} />
      <LineGlow geo={obj.geometry} color={cue.color} size={0.14} refObj={glow} />
      <points ref={links} geometry={beadGeo}>
        <pointsMaterial {...sparkMat(cue.color, cue.size || 0.09)} />
      </points>
    </>
  );
}

// ── 18 · COIN — chance, resolved in public ─────────────────────────
// SHADOW GAMBLING is nine Tomes, three Lucky Dragon Imperas, one
// U'hlti and an Affinity — and J'ynxi is watching all of it. The
// tension of a gamble is entirely in the HANG: the coin has to be in
// the air long enough that you want to know, and then it has to LAND,
// hard, with a decision in it.
//
// So the timeline is deliberately lopsided — 70% of the cue is flight
// and spin, and the last 30% is a flat, still, very bright disc. That
// stillness at the end is the result being read.
function Coin({ cue, onDone }) {
  const grp = useRef();
  const disc = useRef();
  const flash = useRef();
  const st = useRef(newClock());
  const geo = useMemo(() => new THREE.CylinderGeometry(1, 1, 0.09, 24, 1), []);
  useDispose(geo);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!grp.current) return;
    if (t < 0) { grp.current.visible = false; return; }
    grp.current.visible = t < 1;
    if (t >= 1) return;
    const air = Math.min(1, t / 0.7);
    // One clean parabola. E.bump is the arc; the coin is at its apex
    // at exactly half the flight, which is where the eye expects it.
    const h = E.bump(air) * (cue.r || 1.5);
    if (disc.current) {
      disc.current.position.y = h;
      // Spin decays into rest rather than stopping dead — the last
      // half-turn is what makes it feel like it settled.
      const spins = (cue.spin || 7) * Math.PI * 2;
      // E.expo has a harder shoulder than E.out — the coin spins fast,
      // then the rotation arrests. Coupled with the wobble below (which
      // decays to zero at the same moment), it reads as a disc losing
      // its angular momentum against the felt rather than a mesh whose
      // animation ended.
      disc.current.rotation.x = E.expo(air) * spins;
      disc.current.rotation.z = Math.sin(air * 4.2) * 0.22 * (1 - air);
      const s = (cue.size || 0.3) * (1 + (1 - air) * 0.15);
      disc.current.scale.setScalar(s);
      disc.current.material.opacity = t < 0.9 ? 1 : 1 - (t - 0.9) / 0.1;
    }
    // The landing flash fires once, at touchdown, and only then.
    if (flash.current) {
      const land = t < 0.66 ? 0 : Math.max(0, 1 - (t - 0.66) / 0.34);
      const fs = (cue.size || 0.3) * (2.2 + (1 - land) * 5.5);
      flash.current.scale.set(fs, fs, 1);
      flash.current.material.opacity = land * 0.9;
    }
  });
  const pos = cue.to || cue.from || [0, 0, 0];
  return (
    <group ref={grp} position={[pos[0], Y.seam + 0.02, pos[2]]} visible={false}>
      <mesh ref={disc} geometry={geo}>
        <meshBasicMaterial {...glowMat(cue.color, 0)} />
      </mesh>
      <mesh ref={flash} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.006, 0]}>
        <planeGeometry args={[2, 2]} />
        <meshBasicMaterial {...glowMat(cue.color, 0)} map={ringTex()} />
      </mesh>
    </group>
  );
}

// ── 19 · CRACK — the ground gives up ───────────────────────────────
// Calamity, Doom, Apocalypse, Natural Disaster, None More Than I. The
// Dragon Ilk's loudest cards all describe damage done to the WORLD
// rather than to a card, and until now the only thing on the board
// that could say that was the Wave — which passes over. A crack stays.
//
// Branching fissures race outward from a point, each with a seeded
// kink, drawn progressively so they TEAR rather than appear. One
// LineSegments for every branch and every segment: a single draw call
// for the whole fracture.
const CRACK_STEPS = 7;
function Crack({ cue, onDone }) {
  const st = useRef(newClock());
  const glow = useRef();
  const n = Math.max(3, cue.n || 6);
  const R = cue.r || 2.4;
  const obj = useMemo(() => {
    const rr = rnd(cue.id || "crack");
    const g = new THREE.BufferGeometry();
    const verts = new Float32Array(n * CRACK_STEPS * 2 * 3);
    let v = 0;
    for (let b = 0; b < n; b++) {
      const base = (b / n) * Math.PI * 2 + rr() * 0.5;
      let x = 0, z = 0, ang = base;
      for (let s = 0; s < CRACK_STEPS; s++) {
        const step = (R / CRACK_STEPS) * (0.7 + rr() * 0.7);
        const nx = x + Math.cos(ang) * step;
        const nz = z + Math.sin(ang) * step;
        verts[v++] = x; verts[v++] = 0; verts[v++] = z;
        verts[v++] = nx; verts[v++] = 0; verts[v++] = nz;
        x = nx; z = nz;
        ang += (rr() - 0.5) * 0.85; // the kink — a straight crack is a spoke
      }
    }
    g.setAttribute("position", new THREE.BufferAttribute(verts, 3));
    const m = new THREE.LineBasicMaterial({
      color: cue.color, transparent: true, opacity: 0,
      depthWrite: false, toneMapped: false, blending: THREE.AdditiveBlending,
    });
    const L = new THREE.LineSegments(g, m);
    L.visible = false;
    return L;
  }, [cue.id, n, R, cue.color]);
  useDispose(obj);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!obj) return;
    if (t < 0) { obj.visible = false; return; }
    obj.visible = t < 1;
    if (t >= 1) return;
    // The tear races out over the first third and then the light in it
    // dies slowly — the fissure is still there, it just stops glowing.
    const race = Math.min(1, t / 0.34);
    const total = n * CRACK_STEPS * 2;
    const shown = Math.max(2, Math.floor(total * E.out(race)) & ~1);
    obj.geometry.setDrawRange(0, shown);
    obj.material.opacity = t < 0.4 ? 1 : 1 - (t - 0.4) / 0.6;
    if (glow.current) {
      glow.current.geometry.setDrawRange(0, shown);
      glow.current.material.opacity = obj.material.opacity * 0.42;
    }
  });
  const pos = cue.to || cue.from || [0, 0, 0];
  return (
    <group position={[pos[0], Y.seam + 0.007, pos[2]]}>
      <primitive object={obj} />
      <LineGlow geo={obj.geometry} color={cue.color} size={0.1} refObj={glow} />
    </group>
  );
}

// ── 20 · VORTEX — something is pulled out of somewhere ─────────────
// ANCIENT SORCERY (9 Tomes) and RITUAL (9 more) are both SEARCH: reach
// into the Grimoire or the Necropolis and bring back the right page.
// That is a very specific motion and none of the thirteen had it —
// motes scatter, stream travels, but neither one REACHES IN.
//
// A flat spiral of points winding inward to a bright throat, with a
// counter-rotating ring. `tone: "out"` reverses it, which is a thing
// being put back (Blood in Blood Out sends before it fetches, and the
// score gets to say both halves with one primitive).
function Vortex({ cue, onDone }) {
  const pts = useRef();
  const ring = useRef();
  const st = useRef(newClock());
  const n = cue.n || 60;
  const R = cue.r || 1.3;
  const outward = cue.tone === "out";
  const { geo, seeds, arr } = useMemo(() => {
    const rr = rnd(cue.id || "vortex");
    const s = Array.from({ length: n }, () => ({
      a0: rr() * Math.PI * 2,
      rad: 0.25 + rr() * 0.75,
      y: rr() * 0.5,
      ph: rr(),
      sw: 2.5 + rr() * 3.5,
    }));
    const a = new Float32Array(n * 3);
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(a, 3));
    return { geo: g, seeds: s, arr: a };
  }, [cue.id, n]);
  useDispose(geo);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!pts.current) return;
    if (t < 0) { pts.current.visible = false; return; }
    pts.current.visible = t < 1;
    if (t >= 1) return;
    for (let i = 0; i < n; i++) {
      const s = seeds[i];
      // Each particle runs its own loop, offset by phase, so the funnel
      // is continuous instead of pulsing all at once.
      const f = (t * 1.15 + s.ph) % 1;
      const k = outward ? f : 1 - f;
      const rad = R * s.rad * k;
      const a = s.a0 + (1 - k) * (cue.spin || 5.5);
      arr[i * 3] = Math.cos(a) * rad;
      arr[i * 3 + 1] = 0.05 + s.y * k * (cue.size || 0.9) + (1 - k) * 0.18;
      arr[i * 3 + 2] = Math.sin(a) * rad;
    }
    geo.attributes.position.needsUpdate = true;
    pts.current.material.opacity = t < 0.7 ? 1 : 1 - (t - 0.7) / 0.3;
    if (ring.current) {
      const rr2 = R * (outward ? E.out(t) : 1 - E.out(t) * 0.8);
      ring.current.scale.set(Math.max(0.05, rr2), Math.max(0.05, rr2), 1);
      ring.current.rotation.z += dt * (outward ? -2.2 : 2.2);
      ring.current.material.opacity = E.bump(t) * 0.8;
    }
  });
  const pos = cue.to || cue.from || [0, 0, 0];
  return (
    <group position={[pos[0], Y.seam + 0.012, pos[2]]}>
      <points ref={pts} geometry={geo} visible={false}>
        <pointsMaterial {...sparkMat(cue.color, cue.size2 || 0.065)} />
      </points>
      <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[2, 2]} />
        <meshBasicMaterial {...glowMat(cue.color, 0)} map={ringTex()} />
      </mesh>
    </group>
  );
}

// ── 21 · EMBERS — heat rising off something ────────────────────────
// The warm counterpart to motes, and the difference is GRAVITY: motes
// travel in a straight line on a seeded vector and die; embers rise,
// slow, drift sideways, and flicker. That is a fire, a ritual, an
// Anima gain — anything that is alive rather than kinetic.
//
// The flicker is per-particle and deliberately arrhythmic. A synchronised
// flicker reads as a strobe; an unsynchronised one reads as heat.
function Embers({ cue, onDone }) {
  const pts = useRef();
  const st = useRef(newClock());
  const n = cue.n || 30;
  const R = cue.r || 0.9;
  const H = cue.size2 || 1.5;
  const { geo, seeds, arr, alpha } = useMemo(() => {
    const rr = rnd(cue.id || "embers");
    const s = Array.from({ length: n }, () => ({
      x: (rr() - 0.5) * 2, z: (rr() - 0.5) * 2,
      rise: 0.55 + rr() * 0.75, lag: rr() * 0.45,
      sway: 1.5 + rr() * 4, swayAmp: 0.1 + rr() * 0.3,
      fl: 5 + rr() * 12, ph: rr() * 6.28,
    }));
    const a = new Float32Array(n * 3);
    const al = new Float32Array(n);
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(a, 3));
    return { geo: g, seeds: s, arr: a, alpha: al };
  }, [cue.id, n]);
  useDispose(geo);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!pts.current) return;
    if (t < 0) { pts.current.visible = false; return; }
    pts.current.visible = t < 1;
    if (t >= 1) return;
    let lit = 0;
    for (let i = 0; i < n; i++) {
      const s = seeds[i];
      const k = Math.max(0, Math.min(1, (t - s.lag) / (1 - s.lag)));
      // Rise decelerates — E.out is exactly the shape of something
      // losing its buoyancy as it cools.
      const y = E.out(k) * s.rise * H;
      arr[i * 3] = s.x * R + Math.sin(t * s.sway + s.ph) * s.swayAmp * k;
      arr[i * 3 + 1] = 0.06 + y;
      arr[i * 3 + 2] = s.z * R + Math.cos(t * s.sway * 0.8 + s.ph) * s.swayAmp * k;
      alpha[i] = (1 - k) * (0.6 + Math.sin(t * s.fl + s.ph) * 0.4);
      lit += alpha[i];
    }
    geo.attributes.position.needsUpdate = true;
    // Points share one material opacity, so the per-particle flicker is
    // averaged into the whole. Cheaper than a shader and, at these
    // counts, reads the same.
    pts.current.material.opacity = Math.min(1, (lit / n) * 1.5);
  });
  const pos = cue.to || cue.from || [0, 0, 0];
  return (
    <points ref={pts} geometry={geo} position={pos} visible={false}>
      <pointsMaterial {...sparkMat(cue.color, cue.size || 0.07)} />
    </points>
  );
}

// ── 22 · LATTICE — a thing is BUILT ────────────────────────────────
// CONSTRUCTION, the first school: "it is built, and then it stays
// built." Nine Tomes whose whole identity is permanence, and every
// primitive in the original thirteen is transient by construction. So
// this one ASSEMBLES: edges fly in from scattered positions, snap
// square, and hold at full brightness before releasing.
//
// The snap is the moment. Everything before it is approach.
function Lattice({ cue, onDone }) {
  const inst = useRef();
  const st = useRef(newClock());
  const R = cue.r || 0.85;
  const bars = Math.max(4, cue.n || 12);
  const { geo, seeds, dummy, targets } = useMemo(() => {
    const rr = rnd(cue.id || "lattice");
    // The twelve edges of a cube, as (center, axis, length) — cheaper
    // than EdgesGeometry and it lets every bar animate independently.
    const T = [];
    const s = R;
    for (const ax of [0, 1, 2]) {
      for (const a of [-1, 1]) {
        for (const b of [-1, 1]) {
          const c = [0, 0, 0];
          c[(ax + 1) % 3] = a * s;
          c[(ax + 2) % 3] = b * s;
          T.push({ c, ax });
        }
      }
    }
    const seedList = T.slice(0, bars).map(() => ({
      ox: (rr() - 0.5) * 4, oy: 0.8 + rr() * 2.2, oz: (rr() - 0.5) * 4,
      lag: rr() * 0.3, rx: (rr() - 0.5) * 6, ry: (rr() - 0.5) * 6,
    }));
    return {
      geo: new THREE.BoxGeometry(1, 1, 1),
      seeds: seedList, dummy: new THREE.Object3D(),
      targets: T.slice(0, bars),
    };
  }, [cue.id, R, bars]);
  useDispose(geo);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!inst.current) return;
    if (t < 0) { inst.current.visible = false; return; }
    inst.current.visible = t < 1;
    if (t >= 1) return;
    const th = cue.size || 0.055;
    for (let i = 0; i < targets.length; i++) {
      const s = seeds[i]; const T = targets[i];
      // Assemble over the first 60%, hold for the rest. E.out means the
      // bars decelerate INTO position, which is what makes it a snap
      // rather than a slide.
      // E.back, not E.out: the bars overshoot their target by a few
      // percent and settle. That tiny correction at the end is the
      // whole difference between twelve bars sliding into a box and a
      // structure SNAPPING square — which is the one word ("built")
      // this entire school is trying to say.
      const k = E.back(Math.max(0, Math.min(1, (t - s.lag) / (0.6 - s.lag + 0.001))));
      dummy.position.set(
        T.c[0] + s.ox * (1 - k),
        T.c[1] + s.oy * (1 - k),
        T.c[2] + s.oz * (1 - k),
      );
      dummy.rotation.set(s.rx * (1 - k), s.ry * (1 - k), 0);
      const L = R * 2 * k;
      dummy.scale.set(
        T.ax === 0 ? L : th, T.ax === 1 ? L : th, T.ax === 2 ? L : th,
      );
      dummy.updateMatrix();
      inst.current.setMatrixAt(i, dummy.matrix);
    }
    inst.current.instanceMatrix.needsUpdate = true;
    // Bright on arrival (the snap), then it settles to a steady glow
    // and releases — built, and then it stays built.
    const settled = t > 0.58 && t < 0.7 ? 1 : 0.7;
    inst.current.material.opacity = (t < 0.78 ? 1 : 1 - (t - 0.78) / 0.22) * settled;
  });
  const pos = cue.to || cue.from || [0, 0, 0];
  return (
    <instancedMesh ref={inst} args={[geo, undefined, bars]}
      position={[pos[0], pos[1] + R * 0.5, pos[2]]} visible={false}>
      <meshBasicMaterial {...glowMat(cue.color, 0)} />
    </instancedMesh>
  );
}

// ── 23 · HALO — a crown, held above something ──────────────────────
// The four AFFINITIES, the Supreme Being, Intangibility, and every
// "this being is elevated now" moment in the game. Two rings on
// different axes, tilted and counter-rotating, hanging over the card.
//
// This is the only primitive in the file whose whole job is STATUS
// rather than event — so it opens fast, holds long, and leaves slowly.
// Everything else here punctuates; the halo declares.
function Halo({ cue, onDone }) {
  const grp = useRef();
  const a = useRef();
  const b = useRef();
  const st = useRef(newClock());
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!grp.current) return;
    if (t < 0) { grp.current.visible = false; return; }
    grp.current.visible = t < 1;
    if (t >= 1) return;
    const open = E.snap(Math.min(1, t / 0.22));
    const fade = t < 0.62 ? 1 : 1 - (t - 0.62) / 0.38;
    const R = (cue.r || 0.9) * open;
    grp.current.position.y = (cue.from?.[1] ?? 0) + (cue.size || 0.75) + Math.sin(t * 3.1) * 0.05;
    if (a.current) {
      a.current.scale.set(R, R, 1);
      a.current.rotation.z += dt * (cue.spin || 1.4);
      a.current.material.opacity = fade * 0.95;
    }
    if (b.current) {
      b.current.scale.set(R * 0.72, R * 0.72, 1);
      b.current.rotation.z -= dt * (cue.spin || 1.4) * 1.7;
      b.current.material.opacity = fade * 0.7;
    }
  });
  const pos = cue.to || cue.from || [0, 0, 0];
  return (
    <group ref={grp} position={[pos[0], pos[1] + (cue.size || 0.75), pos[2]]} visible={false}>
      {/* Near-flat, seen edge-on from the TABLE preset and as a full
          ring from ORBIT — which is exactly how a crown should read. */}
      <mesh ref={a} rotation={[-Math.PI / 2 + 0.34, 0, 0]}>
        <planeGeometry args={[2, 2]} />
        <meshBasicMaterial {...glowMat(cue.color, 0)} map={ringTex()} />
      </mesh>
      <mesh ref={b} rotation={[-Math.PI / 2 + 0.34, 0.9, 0.5]}>
        <planeGeometry args={[2, 2]} />
        <meshBasicMaterial {...glowMat(cue.color, 0)} map={ringTex()} />
      </mesh>
    </group>
  );
}

// ── 24 · STAR — one hard point of light ────────────────────────────
// The cheapest possible punctuation and the most useful: two crossed
// billboarded blades that snap to full length and collapse. Every
// Space card, every critical moment, every "and THAT is the hit."
//
// The trick is the ratio — the blades are ~30:1, so what the eye
// registers is not two rectangles but a single anisotropic flare. And
// the collapse is much faster than the snap, which is what makes it
// read as light rather than as geometry.
function Star({ cue, onDone }) {
  const grp = useRef();
  const h = useRef();
  const v = useRef();
  const core = useRef();
  const st = useRef(newClock());
  const { camera } = useThree();
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!grp.current) return;
    if (t < 0) { grp.current.visible = false; return; }
    grp.current.visible = t < 1;
    if (t >= 1) return;
    grp.current.lookAt(camera.position);
    grp.current.rotation.z = (cue.tone ?? 0) * (Math.PI / 180);
    const snap = E.snap(Math.min(1, t / 0.14));
    const die = t < 0.14 ? 1 : Math.max(0, 1 - (t - 0.14) / 0.86);
    const R = (cue.r || 1.2) * snap * (0.35 + die * 0.65);
    const w = (cue.size || 0.05);
    if (h.current) { h.current.scale.set(R, w * (0.4 + die * 0.6), 1); h.current.material.opacity = die; }
    if (v.current) { v.current.scale.set(w * (0.4 + die * 0.6), R * 0.62, 1); v.current.material.opacity = die * 0.9; }
    if (core.current) {
      const c = w * 5.5 * (0.5 + die * 0.8);
      core.current.scale.set(c, c, 1);
      core.current.material.opacity = die;
    }
  });
  return (
    <group ref={grp} position={cue.to || cue.from} visible={false}>
      {/* The blades carry the anisotropic streak texture, which is
          the whole difference between "two crossed rectangles" and a
          flare. The core is a glow, not a disc. */}
      <mesh ref={h}><planeGeometry args={[1, 1]} /><meshBasicMaterial {...glowMat(cue.color, 0)} map={bladeTex()} /></mesh>
      <mesh ref={v}><planeGeometry args={[1, 1]} /><meshBasicMaterial {...glowMat(cue.color, 0)} map={bladeTex()} /></mesh>
      <mesh ref={core}><planeGeometry args={[2, 2]} /><meshBasicMaterial {...glowMat(cue.color, 0)} map={glowTex()} /></mesh>
    </group>
  );
}

// ── 25 · RIFT — space is opened, and then it isn't ─────────────────
// The Space Ilk's signature, and the honest visual for every card that
// moves something between zones: Not Here, Not Now, Not Ever!, the
// Graveshift, a Xulan stepping out of the Void.
//
// A vertical tear: a bright core line that grows first, then the tear
// WIDENS around it, holds, and shuts in half the time it opened. The
// asymmetry matters — anything that closes as slowly as it opened
// reads as a curtain, and this is supposed to read as a wound in
// space that space is repairing.
function Rift({ cue, onDone }) {
  const grp = useRef();
  const lip = useRef();
  const core = useRef();
  const st = useRef(newClock());
  const { camera } = useThree();
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!grp.current) return;
    if (t < 0) { grp.current.visible = false; return; }
    grp.current.visible = t < 1;
    if (t >= 1) return;
    grp.current.lookAt(camera.position);
    const H = cue.r || 1.5;
    // core: 0→22%. lip: 12→55%. shut: 72→100%.
    const coreK = E.snap(Math.min(1, t / 0.22));
    const openK = E.out(Math.max(0, Math.min(1, (t - 0.12) / 0.43)));
    const shut = t < 0.72 ? 1 : 1 - E.snap((t - 0.72) / 0.28);
    if (core.current) {
      core.current.scale.set((cue.size || 0.035) * (1 + openK * 0.5), H * coreK * shut, 1);
      core.current.material.opacity = shut;
    }
    if (lip.current) {
      lip.current.scale.set((cue.size2 || 0.34) * openK * shut, H * 0.94 * coreK * shut, 1);
      lip.current.material.opacity = shut * 0.42;
    }
    grp.current.rotation.z += (cue.spin || 0) * dt;
  });
  const pos = cue.to || cue.from || [0, 0, 0];
  return (
    <group ref={grp} position={[pos[0], pos[1] + (cue.r || 1.5) * 0.35, pos[2]]} visible={false}>
      {/* Rotated 90° in UV terms: the blade texture's hot axis runs
          along the tear, so the rift is brightest at its waist and
          fades to nothing at both points. A hard-edged rectangle here
          read as a domino. */}
      <mesh ref={lip}><planeGeometry args={[1, 1]} /><meshBasicMaterial {...glowMat(cue.color, 0)} map={glowTex()} /></mesh>
      <mesh ref={core}><planeGeometry args={[1, 1]} /><meshBasicMaterial {...glowMat(cue.color, 0)} map={bladeTex()} /></mesh>
    </group>
  );
}

// ── 26 · ECHOES — it already happened ──────────────────────────────
// The Time Ilk had a POSE for this (H21's three strobed lunges) and
// nothing in the world to match it. Echoes are the world half: ghost
// copies staggered backward along a direction, each one dimmer and
// each one leaving later, so the trail resolves into the present.
//
// Reads correctly in both directions — `from` → `to` for something
// arriving out of its own past, `to` → `from` for something leaving
// into its future — which is why every Z4Z and DG-0 score can use it.
function Echoes({ cue, onDone }) {
  const grp = useRef();
  const ghosts = useRef([]);
  const st = useRef(newClock());
  const { camera } = useThree();
  const n = Math.max(2, cue.n || 4);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!grp.current) return;
    if (t < 0) { grp.current.visible = false; return; }
    grp.current.visible = t < 1;
    if (t >= 1) return;
    for (let i = 0; i < n; i++) {
      const g = ghosts.current[i];
      if (!g) continue;
      // Each ghost is one beat behind the last. The stagger IS the
      // effect; without it this is four overlapping rectangles.
      const lag = (i / n) * (cue.tone ?? 0.42);
      const k = Math.max(0, Math.min(1, (t - lag) / (1 - lag)));
      const p = lerp3(cue.from, cue.to, E.out(k));
      g.position.set(p[0], p[1], p[2]);
      g.lookAt(camera.position);
      const s = (cue.size || 0.55) * (1 - i * 0.11);
      g.scale.set(s, s * 1.42, 1);
      g.material.opacity = (1 - i / n) * 0.75 * (k < 0.2 ? k / 0.2 : 1 - (k - 0.2) / 0.8);
    }
  });
  return (
    <group ref={grp} visible={false}>
      {Array.from({ length: n }).map((_, i) => (
        <mesh key={i} ref={(m) => { ghosts.current[i] = m; }}>
          <planeGeometry args={[1, 1]} />
          <meshBasicMaterial {...glowMat(cue.color, 0)} map={glowTex()} />
        </mesh>
      ))}
    </group>
  );
}

// ── 27 · SEVER — one cut, all the way through ──────────────────────
// The Weeping Dagger, Solomon Godfry's Manslaughter, X'aol, every
// "ruin 1 being and its Soul." A slash is a WOUND — a thing survives
// a slash. A sever is a DECISION, and the tell is that the two halves
// separate afterward.
//
// Structurally: a blade sweeps through in 20% of the cue, holds as a
// hairline for a beat, and then the two halves drift apart along the
// cut's normal. That last beat is the whole primitive; without the
// drift this is a bright line.
function Sever({ cue, onDone }) {
  const grp = useRef();
  const blade = useRef();
  const top = useRef();
  const bot = useRef();
  const st = useRef(newClock());
  const { camera } = useThree();
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!grp.current) return;
    if (t < 0) { grp.current.visible = false; return; }
    grp.current.visible = t < 1;
    if (t >= 1) return;
    grp.current.lookAt(camera.position);
    grp.current.rotation.z = (cue.tone ?? -18) * (Math.PI / 180);
    // Anticipation. The blade pulls back a fraction before it sweeps —
    // the only primitive that gets to wind up, because a sever is the
    // execution verb and an execution that happens instantly reads as
    // an accident. (The Dragon's Maul is the same idea in the strike
    // language: "it winds up, you get to watch it decide.")
    const cut = E.anticip(Math.min(1, t / 0.2));
    const L = (cue.r || 1.8) * cut;
    if (blade.current) {
      blade.current.scale.set(L, (cue.size || 0.045) * (t < 0.2 ? 1 : 0.45), 1);
      blade.current.material.opacity = t < 0.24 ? 1 : Math.max(0, 1 - (t - 0.24) / 0.5);
    }
    // The halves part after the cut lands, and keep parting. Slow, and
    // it does not come back.
    const part = Math.max(0, (t - 0.26) / 0.74);
    const gap = E.out(part) * (cue.size2 || 0.3);
    const half = (cue.r || 1.8) * 0.5;
    const alpha = Math.max(0, 1 - part * 1.1) * 0.5;
    if (top.current) {
      top.current.position.set(0, gap, 0);
      top.current.scale.set(L, half * 0.5, 1);
      top.current.material.opacity = alpha;
    }
    if (bot.current) {
      bot.current.position.set(0, -gap, 0);
      bot.current.scale.set(L, half * 0.5, 1);
      bot.current.material.opacity = alpha;
    }
  });
  return (
    <group ref={grp} position={cue.to || cue.from} visible={false}>
      <mesh ref={top}><planeGeometry args={[1, 1]} /><meshBasicMaterial {...glowMat(cue.color, 0)} map={bladeTex()} /></mesh>
      <mesh ref={bot}><planeGeometry args={[1, 1]} /><meshBasicMaterial {...glowMat(cue.color, 0)} map={bladeTex()} /></mesh>
      <mesh ref={blade}><planeGeometry args={[1, 1]} /><meshBasicMaterial {...glowMat(cue.color, 0)} map={bladeTex()} /></mesh>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PART II.6 — THE LAST FIVE · H24 pass 3
// ═══════════════════════════════════════════════════════════════════
// Twenty-seven was enough to say every card once. These five exist
// because five specific IMAGES kept getting approximated by something
// close-but-wrong while the library was being written, and an
// approximation repeated forty times stops being an approximation and
// becomes the house style:
//
//   CONSTELLATION  "these things are connected"   — was: a lattice, which
//                  means BUILT and is a completely different claim
//   MIRROR         "it came back at you"          — was: a beam and a veil
//                  in sequence, which reads as two events, not one
//   PLUME          "something is seeping"         — was: embers, which
//                  rise and brighten; seeping falls and spreads
//   THORN          "the ground turned hostile"    — was: a crack, which
//                  is damage TO the floor, not damage FROM it
//   SCAN           "it is looking for something"  — was: a vortex, which
//                  is the FINDING, with no searching before it
//
// Thirty-two total. Same laws as every primitive above: geometry in a
// useMemo handed to useDispose, imperative on refs, zero setState, no
// Math.random, invisible until due, retires itself exactly once.

// ── 28 · CONSTELLATION — separate points discover each other ───────
// Space's other verb. Points appear scattered, then LINES draw between
// them in sequence — and the order matters enormously: if the lines are
// there from the first frame it is a diagram, and if the points arrive
// last it is a net. Points first, then the connections, one at a time.
const CONST_N = 7;
function Constellation({ cue, onDone }) {
  const pts = useRef();
  const glow = useRef();
  const st = useRef(newClock());
  const n = Math.min(12, Math.max(3, cue.n || CONST_N));
  const R = cue.r || 1.2;
  const { geo, nodes, arr, lines } = useMemo(() => {
    const rr = rnd(cue.id || "const");
    const ns = [];
    for (let i = 0; i < n; i++) {
      // Distributed by angle rather than fully random, so the figure
      // always reads as a shape and never as a clump.
      const a = (i / n) * Math.PI * 2 + (rr() - 0.5) * 0.7;
      const rad = R * (0.45 + rr() * 0.55);
      ns.push([Math.cos(a) * rad, 0.1 + rr() * R * 0.5, Math.sin(a) * rad]);
    }
    const a2 = new Float32Array(n * 3);
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(a2, 3));
    // The links: each node to the next, closing the ring. One
    // LineSegments for all of them.
    const lg = new THREE.BufferGeometry();
    const la = new Float32Array(n * 6);
    for (let i = 0; i < n; i++) {
      const p = ns[i]; const q = ns[(i + 1) % n];
      la[i * 6] = p[0]; la[i * 6 + 1] = p[1]; la[i * 6 + 2] = p[2];
      la[i * 6 + 3] = q[0]; la[i * 6 + 4] = q[1]; la[i * 6 + 5] = q[2];
    }
    lg.setAttribute("position", new THREE.BufferAttribute(la, 3));
    const lm = new THREE.LineBasicMaterial({
      color: cue.color, transparent: true, opacity: 0,
      depthWrite: false, toneMapped: false, blending: THREE.AdditiveBlending,
    });
    const L = new THREE.LineSegments(lg, lm);
    L.visible = false;
    return { geo: g, nodes: ns, arr: a2, lines: L };
  }, [cue.id, n, R, cue.color]);
  useDispose(geo, lines);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!pts.current) return;
    if (t < 0) { pts.current.visible = false; lines.visible = false; return; }
    pts.current.visible = t < 1;
    lines.visible = t < 1;
    if (t >= 1) return;
    // Nodes settle inward from slightly further out over the first 30%.
    const settle = E.out(Math.min(1, t / 0.3));
    for (let i = 0; i < n; i++) {
      const k = 1.35 - settle * 0.35;
      arr[i * 3] = nodes[i][0] * k;
      arr[i * 3 + 1] = nodes[i][1] * k;
      arr[i * 3 + 2] = nodes[i][2] * k;
    }
    geo.attributes.position.needsUpdate = true;
    pts.current.material.opacity = t < 0.7 ? Math.min(1, t * 5) : 1 - (t - 0.7) / 0.3;
    // The links draw one at a time from 25% to 75% — the sequence IS
    // the meaning. drawRange steps in pairs because these are segments.
    const link = Math.max(0, Math.min(1, (t - 0.25) / 0.5));
    const seg = Math.floor(n * link) * 2;
    lines.geometry.setDrawRange(0, seg);
    lines.material.opacity = (t < 0.75 ? 1 : 1 - (t - 0.75) / 0.25) * 0.8;
    if (glow.current) {
      glow.current.geometry.setDrawRange(0, seg);
      glow.current.material.opacity = lines.material.opacity * 0.4;
    }
  });
  const pos = cue.to || cue.from || [0, 0, 0];
  return (
    <group position={pos}>
      <points ref={pts} geometry={geo} visible={false}>
        <pointsMaterial {...sparkMat(cue.color, cue.size || 0.1)} />
      </points>
      <primitive object={lines} />
      <LineGlow geo={lines.geometry} color={cue.color} size={0.1} refObj={glow} />
    </group>
  );
}

// ── 29 · MIRROR — it comes back at you ────────────────────────────
// Fate Maker reflects a Lyt Attack. Enlightenment nullifies an
// Affinity — it "answers the answer." Both were being written as a veil
// followed by a beam, which reads as *blocked, and then separately,
// attacked*. A reflection is ONE event and the whole point is that the
// thing that arrives is the thing that left.
//
// So: a plane flashes at the reflection point, and the SAME flash
// travels back out along the incoming path. One shape, two positions,
// and the reversal happens in the middle of its own life.
function Mirror({ cue, onDone }) {
  const grp = useRef();
  const face = useRef();
  const st = useRef(newClock());
  const { camera } = useThree();
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!grp.current) return;
    if (t < 0) { grp.current.visible = false; return; }
    grp.current.visible = t < 1;
    if (t >= 1) return;
    // 0 → 45%: the surface forms and brightens where the hit lands.
    // 45 → 100%: it LEAVES, back along the path it came in on.
    const form = E.snap(Math.min(1, t / 0.45));
    const back = Math.max(0, (t - 0.45) / 0.55);
    const from = cue.from || cue.to || [0, 0, 0];
    const to = cue.to || from;
    const p = lerp3(to, from, E.out(back));
    grp.current.position.set(p[0], p[1], p[2]);
    grp.current.lookAt(camera.position);
    if (face.current) {
      const w = (cue.r || 1.0) * form * (1 - back * 0.55);
      face.current.scale.set(w, w * 1.35, 1);
      // Brightest at the instant of reversal — the flash IS the moment
      // the attack changes owner.
      const flash = back > 0 && back < 0.2 ? 1 : 0.72;
      face.current.material.opacity = form * (1 - back * 0.85) * flash;
    }
  });
  return (
    <group ref={grp} visible={false}>
      <mesh ref={face}>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial {...glowMat(cue.color, 0)} map={glowTex()} />
      </mesh>
    </group>
  );
}

// ── 30 · PLUME — something is seeping ─────────────────────────────
// The dark twin of embers, and the difference is direction and
// spread: embers RISE and brighten (heat, life, Anima gained); a plume
// sinks, widens, and dims (corruption, decay, dread). The Phantom
// scores kept reaching for embers and getting something cheerful.
//
// Particles start high and tight, fall slowly, and drift APART as they
// go — the widening is what makes it read as vapour rather than rain.
function Plume({ cue, onDone }) {
  const pts = useRef();
  const st = useRef(newClock());
  const n = cue.n || 34;
  const R = cue.r || 0.9;
  const { geo, seeds, arr } = useMemo(() => {
    const rr = rnd(cue.id || "plume");
    const s = Array.from({ length: n }, () => ({
      a: rr() * Math.PI * 2, rad: rr(),
      drop: 0.4 + rr() * 0.8, lag: rr() * 0.4,
      sway: 0.8 + rr() * 2.2, ph: rr() * 6.28,
    }));
    const a = new Float32Array(n * 3);
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(a, 3));
    return { geo: g, seeds: s, arr: a };
  }, [cue.id, n]);
  useDispose(geo);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!pts.current) return;
    if (t < 0) { pts.current.visible = false; return; }
    pts.current.visible = t < 1;
    if (t >= 1) return;
    const top = cue.size2 || 1.1;
    for (let i = 0; i < n; i++) {
      const s = seeds[i];
      const k = Math.max(0, Math.min(1, (t - s.lag) / (1 - s.lag)));
      // Spread grows with the fall — a cone, point up.
      const spread = R * s.rad * (0.18 + E.out(k) * 0.9);
      arr[i * 3] = Math.cos(s.a) * spread + Math.sin(t * s.sway + s.ph) * 0.06;
      arr[i * 3 + 1] = 0.05 + top * (1 - E.in2(k)) * s.drop;
      arr[i * 3 + 2] = Math.sin(s.a) * spread + Math.cos(t * s.sway + s.ph) * 0.06;
    }
    geo.attributes.position.needsUpdate = true;
    pts.current.material.opacity =
      (t < 0.25 ? t / 0.25 : 1 - (t - 0.25) / 0.75) * (cue.tone ?? 0.75);
  });
  const pos = cue.to || cue.from || [0, 0, 0];
  return (
    <points ref={pts} geometry={geo} position={pos} visible={false}>
      <pointsMaterial {...sparkMat(cue.color, cue.size || 0.11)} />
    </points>
  );
}

// ── 31 · THORN — the ground turns hostile ─────────────────────────
// A crack is damage done TO the floor. A thorn is damage the floor
// DOES, and the Omnia school and the Chaos Dragon line both want the
// second one — spikes coming up under a card rather than a fissure
// spreading away from it.
//
// Erupts fast (the first 22%), holds, then sinks back. Instanced: one
// draw call for the whole ring of spikes.
function Thorn({ cue, onDone }) {
  const inst = useRef();
  const st = useRef(newClock());
  const n = Math.max(3, cue.n || 8);
  const R = cue.r || 1.0;
  const { geo, seeds, dummy } = useMemo(() => {
    const rr = rnd(cue.id || "thorn");
    // A cone, origin moved to its BASE so scaling y grows it upward out
    // of the felt instead of inflating it around its own middle — the
    // same trick Pillar uses, for the same reason.
    const g = new THREE.ConeGeometry(1, 1, 6, 1);
    g.translate(0, 0.5, 0);
    const s = Array.from({ length: n }, (_, i) => ({
      a: (i / n) * Math.PI * 2 + (rr() - 0.5) * 0.5,
      rad: R * (0.45 + rr() * 0.6),
      h: 0.4 + rr() * 0.75,
      w: 0.07 + rr() * 0.09,
      lag: rr() * 0.22,
      tilt: (rr() - 0.5) * 0.5,
    }));
    return { geo: g, seeds: s, dummy: new THREE.Object3D() };
  }, [cue.id, n, R]);
  useDispose(geo);
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!inst.current) return;
    if (t < 0) { inst.current.visible = false; return; }
    inst.current.visible = t < 1;
    if (t >= 1) return;
    for (let i = 0; i < n; i++) {
      const s = seeds[i];
      const k = Math.max(0, Math.min(1, (t - s.lag) / (1 - s.lag)));
      // Up hard, hold, sink. The hold is what makes it threatening —
      // a spike that appears and immediately leaves is a sparkle.
      const up = k < 0.22 ? E.back(k / 0.22) : (k < 0.7 ? 1 : 1 - (k - 0.7) / 0.3);
      dummy.position.set(Math.cos(s.a) * s.rad, 0, Math.sin(s.a) * s.rad);
      dummy.rotation.set(s.tilt * Math.cos(s.a), 0, -s.tilt * Math.sin(s.a));
      dummy.scale.set(s.w, Math.max(0.001, s.h * up * (cue.size || 1)), s.w);
      dummy.updateMatrix();
      inst.current.setMatrixAt(i, dummy.matrix);
    }
    inst.current.instanceMatrix.needsUpdate = true;
    inst.current.material.opacity = t < 0.62 ? 0.95 : 1 - (t - 0.62) / 0.38;
  });
  const pos = cue.to || cue.from || [0, 0, 0];
  return (
    <instancedMesh ref={inst} args={[geo, undefined, n]}
      position={[pos[0], Y.seam + 0.005, pos[2]]} visible={false}>
      <meshBasicMaterial {...glowMat(cue.color, 0)} />
    </instancedMesh>
  );
}

// ── 32 · SCAN — it is looking for something ───────────────────────
// The Ancient Sorcery school is nine cards of SEARCH, and the vortex is
// the moment the hand closes on the page — the finding. Nothing said
// the looking. A bar sweeps across the card, edge to edge, and a second
// faint one trails it; the sweep is what a search LOOKS like and it is
// completely distinct from the funnel that follows it.
//
// `tone` sets the axis in degrees, so the same primitive sweeps
// horizontally for a Grimoire search and vertically for a Luxmoire one.
function Scan({ cue, onDone }) {
  const grp = useRef();
  const bar = useRef();
  const ghost = useRef();
  const st = useRef(newClock());
  const { camera } = useThree();
  useFrame((_, dt) => {
    const t = cueTick(st, dt, cue, onDone);
    if (!grp.current) return;
    if (t < 0) { grp.current.visible = false; return; }
    grp.current.visible = t < 1;
    if (t >= 1) return;
    grp.current.lookAt(camera.position);
    grp.current.rotation.z = (cue.tone ?? 0) * (Math.PI / 180);
    const W = cue.r || 1.1;
    // Linear, not eased. A search sweep that decelerates looks like it
    // found something early and is showing off; a constant rate reads
    // as a machine covering ground.
    const x = (-1 + t * 2) * W;
    const fade = t < 0.12 ? t / 0.12 : (t > 0.86 ? (1 - t) / 0.14 : 1);
    if (bar.current) {
      bar.current.position.x = x;
      bar.current.scale.set(cue.size || 0.05, W * 1.5, 1);
      bar.current.material.opacity = fade;
    }
    if (ghost.current) {
      ghost.current.position.x = x - W * 0.18;
      ghost.current.scale.set((cue.size || 0.05) * 3.2, W * 1.42, 1);
      ghost.current.material.opacity = fade * 0.22;
    }
  });
  return (
    <group ref={grp} position={cue.to || cue.from} visible={false}>
      <mesh ref={ghost}><planeGeometry args={[1, 1]} /><meshBasicMaterial {...glowMat(cue.color, 0)} map={bladeTex()} /></mesh>
      <mesh ref={bar}><planeGeometry args={[1, 1]} /><meshBasicMaterial {...glowMat(cue.color, 0)} map={bladeTex()} /></mesh>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PART III — THE DISPATCHER
// ═══════════════════════════════════════════════════════════════════
// One name → one primitive. An unknown name renders NOTHING and calls
// onDone immediately, because a typo in a card's score must cost that
// card its sparkle and never the whole board's frame loop.

const FX = {
  // The original thirteen (H23)
  beam: Beam, orb: Orb, slash: Slash, shock: Shock, motes: Motes,
  pillar: Pillar, spiral: Spiral, bolt: Bolt, wave: Wave,
  bloom: Bloom, stream: Stream, glyph: Glyph, grave: Grave,
  // The fourteen more (H24) — the schools, the alignments, the verbs
  rune: Rune, veil: Veil, shatter: Shatter, chain: Chain, coin: Coin,
  crack: Crack, vortex: Vortex, embers: Embers, lattice: Lattice,
  halo: Halo, star: Star, rift: Rift, echoes: Echoes, sever: Sever,
  // The last five (H24 pass 3) — connection, reflection, seepage,
  // hostility from below, and the act of looking.
  constellation: Constellation, mirror: Mirror, plume: Plume,
  thorn: Thorn, scan: Scan,
};

export const FX_NAMES = Object.keys(FX);

function Dead({ cue, onDone }) {
  const st = useRef(newClock());
  useFrame((_, dt) => { cueTick(st, dt, { ...cue, ms: 16 }, onDone); });
  return null;
}

export const FxCue = memo(function FxCue({ cue, onDone }) {
  if (!cue || !cue.fx) return null;
  const C = FX[cue.fx] || Dead;
  return <C cue={cue} onDone={onDone} />;
});

// ═══════════════════════════════════════════════════════════════════
// PART IV — THE SCORE LAYER
// ═══════════════════════════════════════════════════════════════════
// Mounts every live cue in the world and retires each one as it ends.
// Deliberately DUMB: it does not know what a Spectral Tiger is, it
// does not resolve anchors (cardMotion did that before these ever got
// here), and it never reads game state. Hand it cues, it plays cues.
//
// The cap is a storm guard with the same reasoning as the flight cap
// above it: an eight-card chain resolving in one tick must not put 200
// cues in the scene. Newest wins — the thing that just happened is
// always more interesting than the thing that happened 900ms ago.

export const CUE_CAP = 44;

export const ScoreLayer = memo(function ScoreLayer({ cues, onCueDone }) {
  if (!cues || !cues.length) return null;
  return (
    <>
      {cues.map((c) => <FxCue key={c.id} cue={c} onDone={onCueDone} />)}
    </>
  );
});

const fx3D = { FxCue, ScoreLayer, FX_NAMES, CUE_CAP };
export default fx3D;
